import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import Sidebar from '../../components/Sidebar/Sidebar';
import Topbar from '../../components/Topbar/Topbar';
import RightSidebar from '../../components/RightSidebar/RightSidebar';
import { listProjects, getProject, deleteProject, updateProject, createProject, listFirms, createFirm, loadProject, listAttachments, uploadAttachments, deleteAttachment, searchClients, createExternalClient, resolveOrganization, getProjectInvitations, convertDwgToDxf, getStakeholderShares, postNoteReply, deleteNoteReply } from '../../services/projectApi';
import { convertDwgToDxfBrowser } from '../../utils/dwgConverter';
import API_URL from '../../config';
import { JbzViewer, DxfViewer } from '../../modules/dxf';
import EditorPreview from '../../components/EditorPreview/EditorPreview';
import PdfViewer from '../../components/DxfViewer/PdfViewer';
import AutocompleteInput from '../../components/common/AutocompleteInput';
import StakeholderShareCell from '../../components/common/StakeholderShareCell';
import StakeholderAccessCell from '../../components/common/StakeholderAccessCell';
import { resolveAccessConfig as resolveStakeholderAccessConfig, ACCESS_SHEETS } from '../../utils/stakeholderAccess';
import { colorForRoleKey, colorForRoleLabel, resolveStakeholderColor, withAlpha as colorWithAlpha } from '../../utils/stakeholderColors';
import { CITY_LIST, getDistricts } from '../../data/turkeyGeo';
import { getAllDistributionCompanies } from '../../services/distributionCompanyApi';
import { PROJECT_TYPES as EDITOR_TEMPLATE_TYPES, PROJECT_TYPE_LABELS as EDITOR_TEMPLATE_LABELS, detectProjectType } from '../../constants/projectTypes';
import './Projects.css';

// Convert DWG File to DXF text — browser-side first, backend fallback
async function getDxfTextFromDwg(dwgFile) {
  let browserErrMsg = null;
  try {
    return await convertDwgToDxfBrowser(dwgFile);
  } catch (browserErr) {
    browserErrMsg = browserErr instanceof Error ? browserErr.message : String(browserErr);
    console.warn('Browser DWG conversion failed, trying backend:', browserErrMsg);
  }
  try {
    const blob = await convertDwgToDxf(dwgFile);
    return blob.text();
  } catch (backendErr) {
    const backendMsg = backendErr instanceof Error ? backendErr.message : String(backendErr);
    const msg = [browserErrMsg, backendMsg].filter(Boolean).join(' | ');
    throw new Error(msg);
  }
}

function formatSize(bytes) {
  if (!bytes) return '-';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

const PROJECT_TYPES = ['Konut', 'Ticari', 'Sanayi', 'Kamu', 'Diger'];

const FILE_CATEGORIES = [
  { key: 'mimari_dxf',          label: 'Mimari',               accept: '.dxf,.DXF,.dwg,.DWG,.pdf,.PDF,application/dxf,image/vnd.dxf,application/pdf', icon: 'M', uploadRole: 'mimariMuellif' },
  // JBZ — tüm paydaşlar önizler/not ekler; indirme yetkisi disiplinin
  // müellifine (şimdilik elektrikMuellif) sınırlı (canDownloadCat).
  { key: 'jbz',                 label: 'JBZ Dosyaları',        accept: '.jbz,.JBZ', icon: 'J', uploadRole: 'elektrikMuellif' },
  { key: 'elektrik_dxf',        label: 'Kuvvetli Akım',        accept: '.dxf,.DXF,.dwg,.DWG,.pdf,.PDF,application/dxf,image/vnd.dxf,application/pdf', icon: 'K', uploadRole: 'elektrikMuellif' },
  { key: 'zayif_akim_dxf',      label: 'Zayıf Akım',           accept: '.dxf,.DXF,.dwg,.DWG,.pdf,.PDF,application/dxf,image/vnd.dxf,application/pdf', icon: 'Z', uploadRole: 'elektrikMuellif' },
  { key: 'yangin_algilama_dxf', label: 'Yangın Algılama',      accept: '.dxf,.DXF,.dwg,.DWG,.pdf,.PDF,application/dxf,image/vnd.dxf,application/pdf', icon: 'Y', uploadRole: 'elektrikMuellif' },
  { key: 'santiye_dxf',         label: 'Şantiye',              accept: '.dxf,.DXF,.dwg,.DWG,.pdf,.PDF,application/dxf,image/vnd.dxf,application/pdf', icon: 'S', uploadRole: 'elektrikMuellif' },
];

const FIRM_FIELDS = [
  { key: 'yapiDenetimId', type: 'yapi_denetim',    label: 'Yapı Denetim',            emptyLabel: 'Yapı Denetimsiz',           unknownLabel: 'Bilinmeyen Yapı Denetim' },
  { key: 'elektrikDagitimId', type: 'elektrik_dagitim', label: 'Elektrik Dağıtım Firması', emptyLabel: 'Dağıtım Firması Yok',  unknownLabel: 'Bilinmeyen Dağıtım Firması' },
  { key: 'telekomId',    type: 'telekom',           label: 'Telekom',                 emptyLabel: 'Telekom Firması Yok',       unknownLabel: 'Bilinmeyen Telekom' },
  { key: 'itfaiyeId',    type: 'itfaiye',           label: 'İtfaiye',                 emptyLabel: 'İtfaiye Bilinmiyor',        unknownLabel: 'Bilinmeyen İtfaiye' },
  { key: 'belediyeId',   type: 'belediye',          label: 'Belediye',                emptyLabel: 'Belediye Yok',              unknownLabel: 'Bilinmeyen Belediye' },
];

// Her paydaş alanı için arama yapılacak Projabze üye filtreleri.
//   matchSectorRole : sectorRole (party_categories.key) eşleşmesi
//   matchDiscipline : disipline ID eşleşmesi (örn. disc-electrical)
const PERSON_FIELDS = [
  { key: 'malSahibi',        label: 'Mal Sahibi / İşveren', matchSectorRole: 'is_sahibi' },
  { key: 'client',           label: 'Müşteri',              matchSectorRole: 'is_sahibi' },
  { key: 'muteahhit',        label: 'Müteahhit',            matchSectorRole: 'yuklenici' },
  { key: 'mimariMuellif',    label: 'Mimari Müellif',       matchSectorRole: 'muellif', matchDiscipline: 'disc-architectural' },
  { key: 'elektrikMuellif',  label: 'Elektrik Müellif',     matchSectorRole: 'muellif', matchDiscipline: 'disc-electrical' },
  { key: 'mekanikMuellif',   label: 'Mekanik Müellif',      matchSectorRole: 'muellif', matchDiscipline: 'disc-mechanical' },
  { key: 'kontrolMuhendis',  label: 'Kontrol Mühendisi',    matchSectorRole: 'kontrolor' },
];

// Bir kullanıcının (sectorRole + discipline) profilinden hangi PERSON_FIELDS
// key'ine denk geldiğini döner; bulunamazsa null.
function userToPersonFieldKey({ sectorRole, discipline } = {}) {
  if (!sectorRole) return null;
  for (const pf of PERSON_FIELDS) {
    if (pf.matchSectorRole !== sectorRole) continue;
    if (pf.matchDiscipline && pf.matchDiscipline !== discipline) continue;
    return pf.key;
  }
  return null;
}

// Bir paydaş alanı key'inin editMeta üzerindeki gerçek alan adlarını döner.
// 'client' özel bir durumdur (clientType/clientMemberId/clientExternalId).
const personFieldDataKeys = (key) => key === 'client'
  ? { name: 'client', type: 'clientType', memberId: 'clientMemberId', externalId: 'clientExternalId' }
  : { name: key, type: key + 'Type', memberId: key + 'MemberId', externalId: key + 'ExternalId' };

// ─── Combobox: search + select + create new ───
const UNKNOWN_VALUE = '__unknown__';

const FIRM_TYPE_MAP_RESOLVE = {
  yapi_denetim: 'yapi_denetim',
  elektrik_dagitim: 'elektrik_dagitim',
  itfaiye: 'itfaiye',
  telekom: 'telekom',
  belediye: 'belediye',
};

const FirmCombobox = ({ fieldKey, firmType, emptyLabel, unknownLabel, value, firms, onChange, onCreateFirm }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [resolvedOrgName, setResolvedOrgName] = useState('');
  const [highlightIdx, setHighlightIdx] = useState(0);
  const ref = useRef(null);
  const listRef = useRef(null);

  const filtered = query
    ? firms.filter(f => f.name.toLowerCase().includes(query.toLowerCase()))
    : firms;

  const selectedFirm = firms.find(f => f.id === value);
  const showCreate = !!query.trim() && !filtered.some(f => f.name.toLowerCase() === query.toLowerCase());

  // Klavye navigasyonu için flat liste (önce empty + unknown, sonra filtered, sonra create)
  const flatItems = [
    { kind: 'empty' },
    { kind: 'unknown' },
    ...filtered.map(f => ({ kind: 'firm', data: f })),
    ...(showCreate ? [{ kind: 'create' }] : []),
  ];

  useEffect(() => {
    if (!value || value === UNKNOWN_VALUE || !FIRM_TYPE_MAP_RESOLVE[firmType]) { setResolvedOrgName(''); return; }
    resolveOrganization({ firmType: FIRM_TYPE_MAP_RESOLVE[firmType], firmId: value })
      .then(orgs => setResolvedOrgName(orgs.length > 0 ? orgs[0].name : ''))
      .catch(() => setResolvedOrgName(''));
  }, [value, firmType]);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Açılınca veya filtre değişince mevcut value'yu vurgula
  useEffect(() => {
    if (!open) return;
    if (flatItems.length === 0) { setHighlightIdx(0); return; }
    let idx = 0;
    if (!value) idx = 0;
    else if (value === UNKNOWN_VALUE) idx = 1;
    else {
      const fIdx = flatItems.findIndex(it => it.kind === 'firm' && it.data.id === value);
      if (fIdx >= 0) idx = fIdx;
    }
    setHighlightIdx(idx);
  }, [open, query]); // eslint-disable-line react-hooks/exhaustive-deps

  // Vurgulanan satırı görünür yap
  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-fc-idx="${highlightIdx}"]`);
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest' });
  }, [highlightIdx, open]);

  const handleSelect = (firmId) => {
    // ID ile birlikte firma ADINI da geçir (parent metadata'ya *Name olarak yazar).
    const firm = firms.find(f => f.id === firmId);
    onChange(firmId, firm?.name || '');
    setQuery('');
    setOpen(false);
  };

  const handleCreate = async () => {
    if (!query.trim()) return;
    const firm = await onCreateFirm(query.trim(), firmType);
    if (firm) {
      onChange(firm.id, firm.name || '');
      setQuery('');
      setOpen(false);
    }
  };

  const activateItem = (item) => {
    if (!item) return;
    if (item.kind === 'empty') handleSelect('');
    else if (item.kind === 'unknown') handleSelect(UNKNOWN_VALUE);
    else if (item.kind === 'firm') handleSelect(item.data.id);
    else if (item.kind === 'create') handleCreate();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) { setOpen(true); return; }
      if (flatItems.length === 0) return;
      setHighlightIdx(i => Math.min(flatItems.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) return;
      if (flatItems.length === 0) return;
      setHighlightIdx(i => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      if (open && flatItems.length > 0) {
        e.preventDefault();
        activateItem(flatItems[highlightIdx] || flatItems[0]);
      }
    } else if (e.key === 'Escape') {
      if (open) { e.preventDefault(); setOpen(false); }
    }
  };

  const handleTriggerKeyDown = (e) => {
    if ((e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') && !open) {
      e.preventDefault();
      setOpen(true);
    } else if (e.key === 'Escape' && open) {
      e.preventDefault();
      setOpen(false);
    }
  };

  let runningIdx = 0;

  return (
    <div className="prj-combobox" ref={ref}>
      <div
        className="prj-combobox-trigger"
        tabIndex={0}
        onClick={() => setOpen(!open)}
        onKeyDown={handleTriggerKeyDown}
      >
        {(() => {
          const c = colorForRoleKey(fieldKey);
          if (!c) return null;
          return <span className="prj-stakeholder-color-dot" style={{ background: c, marginRight: 6 }} title="Paydas rengi" />;
        })()}
        <span className={value ? 'prj-combobox-val' : 'prj-combobox-placeholder'}>
          {value === UNKNOWN_VALUE ? unknownLabel : selectedFirm ? selectedFirm.name : emptyLabel}
        </span>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9"/></svg>
      </div>
      {resolvedOrgName && (
        <div className="prj-combobox-org-badge">Projabze kayitli: {resolvedOrgName}</div>
      )}
      {open && (
        <div className="prj-combobox-dropdown">
          <input
            className="prj-combobox-search"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ara veya yeni ekle..."
            autoFocus
          />
          <div className="prj-combobox-list" ref={listRef}>
            {(() => { const idx = runningIdx++; return (
              <div
                data-fc-idx={idx}
                className={`prj-combobox-opt${!value ? ' selected' : ''}${idx === highlightIdx ? ' highlight' : ''}`}
                onMouseEnter={() => setHighlightIdx(idx)}
                onMouseDown={(e) => { e.preventDefault(); handleSelect(''); }}
              >
                {emptyLabel}
              </div>
            ); })()}
            {(() => { const idx = runningIdx++; return (
              <div
                data-fc-idx={idx}
                className={`prj-combobox-opt${value === UNKNOWN_VALUE ? ' selected' : ''}${idx === highlightIdx ? ' highlight' : ''}`}
                onMouseEnter={() => setHighlightIdx(idx)}
                onMouseDown={(e) => { e.preventDefault(); handleSelect(UNKNOWN_VALUE); }}
              >
                {unknownLabel}
              </div>
            ); })()}
            {filtered.map(f => {
              const idx = runningIdx++;
              return (
                <div
                  key={f.id}
                  data-fc-idx={idx}
                  className={`prj-combobox-opt${value === f.id ? ' selected' : ''}${idx === highlightIdx ? ' highlight' : ''}`}
                  onMouseEnter={() => setHighlightIdx(idx)}
                  onMouseDown={(e) => { e.preventDefault(); handleSelect(f.id); }}
                >
                  <span>{f.name}</span>
                  {f.city && <span className="prj-combobox-sub">{f.city}{f.district ? ` / ${f.district}` : ''}</span>}
                </div>
              );
            })}
            {showCreate && (() => {
              const idx = runningIdx++;
              return (
                <div
                  data-fc-idx={idx}
                  className={`prj-combobox-opt prj-combobox-create${idx === highlightIdx ? ' highlight' : ''}`}
                  onMouseEnter={() => setHighlightIdx(idx)}
                  onMouseDown={(e) => { e.preventDefault(); handleCreate(); }}
                >
                  + "{query.trim()}" oluştur
                </div>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Client Combobox: search members + external clients + manual entry ───
const ClientCombobox = ({ value, onChange }) => {
  // value = { client, clientType, clientMemberId, clientExternalId }
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState({ members: [], externals: [] });
  const [searching, setSearching] = useState(false);
  const ref = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const doSearch = useCallback(async (q) => {
    if (!q.trim()) { setResults({ members: [], externals: [] }); return; }
    try {
      setSearching(true);
      const data = await searchClients(q);
      setResults(data);
    } catch { setResults({ members: [], externals: [] }); }
    finally { setSearching(false); }
  }, []);

  const handleQueryChange = (q) => {
    setQuery(q);
    // Also update the client name live for manual entry
    onChange({ client: q, clientType: 'manual', clientMemberId: '', clientExternalId: '' });
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => doSearch(q), 300);
  };

  const selectMember = (m) => {
    onChange({ client: m.name, clientType: 'member', clientMemberId: m.id, clientExternalId: '' });
    setQuery(m.name);
    setOpen(false);
  };

  const selectExternal = (c) => {
    onChange({ client: c.name, clientType: 'external', clientMemberId: '', clientExternalId: c.id });
    setQuery(c.name);
    setOpen(false);
  };

  const createAndSelect = async () => {
    if (!query.trim()) return;
    try {
      const client = await createExternalClient({ name: query.trim() });
      onChange({ client: client.name, clientType: 'external', clientMemberId: '', clientExternalId: client.id });
      setQuery(client.name);
      setOpen(false);
    } catch (err) {
      console.error('Musteri olusturma hatasi:', err);
    }
  };

  const displayValue = value?.client || '';
  const hasResults = results.members.length > 0 || results.externals.length > 0;
  const exactMatch = [...results.members, ...results.externals].some(r => r.name.toLowerCase() === query.trim().toLowerCase());

  return (
    <div className="prj-client-combo" ref={ref}>
      <input
        className="prj-edit-input"
        type="text"
        value={open ? query : displayValue}
        onFocus={() => { setOpen(true); setQuery(displayValue); if (displayValue) doSearch(displayValue); }}
        onChange={e => handleQueryChange(e.target.value)}
        placeholder="Müşteri adı yazın..."
      />
      {(() => {
        const c = colorForRoleKey('client');
        if (!c) return null;
        return <span className="prj-stakeholder-color-dot" style={{ background: c }} title="Müşteri" />;
      })()}
      {open && (query.trim().length >= 2 || hasResults) && (
        <div className="prj-client-dropdown">
          {searching && <div className="prj-client-searching">Araniyor...</div>}
          {results.members.length > 0 && (
            <>
              <div className="prj-client-group-label">Projabze Uyeleri</div>
              {results.members.map(m => (
                <div key={m.id} className="prj-client-opt" onClick={() => selectMember(m)}>
                  <div className="prj-client-opt-avatar">
                    {m.avatar ? <img src={m.avatar} alt="" /> : <span>{(m.name || '?')[0]}</span>}
                  </div>
                  <div className="prj-client-opt-info">
                    <span className="prj-client-opt-name">{m.name}{m.company ? ` — ${m.company}` : ''}</span>
                    {m.email && <span className="prj-client-opt-sub">{m.email}</span>}
                  </div>
                </div>
              ))}
            </>
          )}
          {results.externals.length > 0 && (
            <>
              <div className="prj-client-group-label">Kayıtlı Müşteriler</div>
              {results.externals.map(c => (
                <div key={c.id} className="prj-client-opt" onClick={() => selectExternal(c)}>
                  <div className="prj-client-opt-avatar ext"><span>{(c.name || '?')[0]}</span></div>
                  <div className="prj-client-opt-info">
                    <span className="prj-client-opt-name">{c.name}{c.company ? ` — ${c.company}` : ''}</span>
                    {c.email && <span className="prj-client-opt-sub">{c.email}</span>}
                  </div>
                </div>
              ))}
            </>
          )}
          {!searching && query.trim().length >= 2 && !exactMatch && (
            <div className="prj-client-opt prj-client-create" onClick={createAndSelect}>
              + "{query.trim()}" dış müşteri olarak kaydet
            </div>
          )}
          {!searching && !hasResults && query.trim().length >= 2 && (
            <div className="prj-client-empty">Sonuç bulunamadı. Manuel girebilir veya dış müşteri olarak kaydedebilirsiniz.</div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Person Combobox: search kişilerim for person-type stakeholders ───
const PersonCombobox = ({ fieldKey, value, onChange, roleFilter }) => {
  // value = { name, type, memberId, externalId }
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState({ members: [], externals: [] });
  const [searching, setSearching] = useState(false);
  const [newContact, setNewContact] = useState(null); // { email, name, company }
  const [saving, setSaving] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(0);
  const ref = useRef(null);
  const dropdownRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setNewContact(null); } };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const doSearch = useCallback(async (q) => {
    if (!q.trim()) { setResults({ members: [], externals: [] }); return; }
    try {
      setSearching(true);
      const data = await searchClients(q, roleFilter || {});
      setResults(data);
    } catch { setResults({ members: [], externals: [] }); }
    finally { setSearching(false); }
  }, [roleFilter]);

  const handleQueryChange = (q) => {
    setQuery(q);
    setNewContact(null);
    onChange({ name: q, type: 'manual', memberId: '', externalId: '' });
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => doSearch(q), 300);
  };

  const selectMember = (m) => {
    onChange({ name: m.name, type: 'member', memberId: m.id, externalId: '' });
    setQuery(m.name);
    setOpen(false);
    setNewContact(null);
  };

  const selectExternal = (c) => {
    onChange({ name: c.name, type: 'external', memberId: '', externalId: c.id });
    setQuery(c.name);
    setOpen(false);
    setNewContact(null);
  };

  const isEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
  const emailInResults = (email) => {
    const e = email.toLowerCase();
    return [...results.members, ...results.externals].some(r => (r.email || '').toLowerCase() === e);
  };

  const handleAddNew = () => {
    setNewContact({ email: query.trim(), name: '', company: '' });
  };

  const handleSaveNew = async () => {
    if (!newContact || !newContact.name.trim()) return;
    try {
      setSaving(true);
      const client = await createExternalClient({
        name: newContact.name.trim(),
        email: newContact.email,
        company: newContact.company.trim(),
      });
      onChange({ name: client.name, type: 'external', memberId: '', externalId: client.id });
      setQuery(client.name);
      setOpen(false);
      setNewContact(null);
    } catch (err) {
      console.error('Kisi kaydetme hatasi:', err);
    } finally { setSaving(false); }
  };

  const displayValue = value?.name || '';
  const hasResults = results.members.length > 0 || results.externals.length > 0;
  const showNewEmailForm = !searching && query.trim().length >= 2 && isEmail(query.trim()) && !emailInResults(query.trim());

  // Klavye navigasyonu için seçilebilir item'ları düz listede topla
  const flatItems = newContact ? [] : [
    ...results.members.map(m => ({ kind: 'member', data: m })),
    ...results.externals.map(c => ({ kind: 'external', data: c })),
    ...(showNewEmailForm ? [{ kind: 'addnew' }] : []),
  ];

  // Filtre / liste değişince highlight'ı düzgün indekse oturt
  useEffect(() => {
    if (flatItems.length === 0) { setHighlightIdx(0); return; }
    if (highlightIdx >= flatItems.length) setHighlightIdx(0);
  }, [results, showNewEmailForm, newContact]); // eslint-disable-line react-hooks/exhaustive-deps

  // Vurgulanan satırı görünür yap
  useEffect(() => {
    if (!open || !dropdownRef.current) return;
    const el = dropdownRef.current.querySelector(`[data-pc-idx="${highlightIdx}"]`);
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest' });
  }, [highlightIdx, open]);

  const activateItem = (item) => {
    if (!item) return;
    if (item.kind === 'member') selectMember(item.data);
    else if (item.kind === 'external') selectExternal(item.data);
    else if (item.kind === 'addnew') handleAddNew();
  };

  const handleKeyDown = (e) => {
    if (newContact) return; // newContact form açıkken parent navigasyonu kapalı
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) { setOpen(true); return; }
      if (flatItems.length === 0) return;
      setHighlightIdx(i => Math.min(flatItems.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) return;
      if (flatItems.length === 0) return;
      setHighlightIdx(i => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      if (open && flatItems.length > 0) {
        e.preventDefault();
        activateItem(flatItems[highlightIdx] || flatItems[0]);
      }
    } else if (e.key === 'Escape') {
      if (open) { e.preventDefault(); setOpen(false); }
    }
  };

  // Akıştaki global index'i hesaplamak için akümülatör
  let runningIdx = 0;

  return (
    <div className="prj-person-combo" ref={ref}>
      <input
        className="prj-edit-input"
        type="text"
        value={open ? query : displayValue}
        onFocus={() => { setOpen(true); setQuery(displayValue); if (displayValue) doSearch(displayValue); }}
        onChange={e => handleQueryChange(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Isim veya e-posta yazin..."
      />
      {(() => {
        const c = resolveStakeholderColor({ key: fieldKey, fallbackSeed: fieldKey });
        if (!c) return null;
        return <span className="prj-stakeholder-color-dot" style={{ background: c }} title="Paydas rengi" />;
      })()}
      {open && (query.trim().length >= 2 || hasResults) && (
        <div className="prj-client-dropdown" ref={dropdownRef}>
          {searching && <div className="prj-client-searching">Araniyor...</div>}
          {results.members.length > 0 && (
            <>
              <div className="prj-client-group-label">Projabze Uyeleri</div>
              {results.members.map(m => {
                const idx = runningIdx++;
                return (
                  <div
                    key={m.id}
                    data-pc-idx={idx}
                    className={`prj-client-opt${idx === highlightIdx ? ' highlight' : ''}`}
                    onMouseEnter={() => setHighlightIdx(idx)}
                    onMouseDown={(e) => { e.preventDefault(); selectMember(m); }}
                  >
                    <div className="prj-client-opt-avatar">
                      {m.avatar ? <img src={m.avatar} alt="" /> : <span>{(m.name || '?')[0]}</span>}
                    </div>
                    <div className="prj-client-opt-info">
                      <span className="prj-client-opt-name">{m.name}{m.company ? ` — ${m.company}` : ''}</span>
                      {m.email && <span className="prj-client-opt-sub">{m.email}{m.city ? ` · ${m.city}` : ''}</span>}
                    </div>
                  </div>
                );
              })}
            </>
          )}
          {results.externals.length > 0 && (
            <>
              <div className="prj-client-group-label">Kayıtlı Kişiler</div>
              {results.externals.map(c => {
                const idx = runningIdx++;
                return (
                  <div
                    key={c.id}
                    data-pc-idx={idx}
                    className={`prj-client-opt${idx === highlightIdx ? ' highlight' : ''}`}
                    onMouseEnter={() => setHighlightIdx(idx)}
                    onMouseDown={(e) => { e.preventDefault(); selectExternal(c); }}
                  >
                    <div className="prj-client-opt-avatar ext"><span>{(c.name || '?')[0]}</span></div>
                    <div className="prj-client-opt-info">
                      <span className="prj-client-opt-name">{c.name}{c.company ? ` — ${c.company}` : ''}</span>
                      {c.email && <span className="prj-client-opt-sub">{c.email}</span>}
                    </div>
                  </div>
                );
              })}
            </>
          )}
          {showNewEmailForm && !newContact && (() => {
            const idx = runningIdx++;
            return (
              <div
                data-pc-idx={idx}
                className={`prj-client-opt prj-client-create${idx === highlightIdx ? ' highlight' : ''}`}
                onMouseEnter={() => setHighlightIdx(idx)}
                onMouseDown={(e) => { e.preventDefault(); handleAddNew(); }}
              >
                + "{query.trim()}" yeni kisi olarak ekle
              </div>
            );
          })()}
          {newContact && (
            <div className="prj-newcontact-form">
              <div className="prj-newcontact-email">{newContact.email}</div>
              <input
                className="prj-newcontact-input"
                type="text"
                placeholder="Ad Soyad *"
                value={newContact.name}
                onChange={e => setNewContact(prev => ({ ...prev, name: e.target.value }))}
                autoFocus
              />
              <input
                className="prj-newcontact-input"
                type="text"
                placeholder="Firma Adi"
                value={newContact.company}
                onChange={e => setNewContact(prev => ({ ...prev, company: e.target.value }))}
              />
              <button
                className="prj-newcontact-save"
                disabled={!newContact.name.trim() || saving}
                onClick={handleSaveNew}
              >
                {saving ? 'Kaydediliyor...' : 'Kaydet ve Sec'}
              </button>
            </div>
          )}
          {!searching && !hasResults && query.trim().length >= 2 && !showNewEmailForm && !newContact && (
            <div className="prj-client-empty">Sonuc bulunamadi. E-posta girerek yeni kisi ekleyebilirsiniz.</div>
          )}
        </div>
      )}
    </div>
  );
};

/* ─── Invitation Status Panel ─── */
const INV_STATUS_LABELS = {
  pending: 'Bekliyor', viewed: 'Goruldu', in_review: 'Inceleniyor',
  approved: 'Onaylandi', rejected: 'Reddedildi', revision_requested: 'Revizyon'
};
const INV_STATUS_COLORS = {
  pending: '#eab308', viewed: '#8b5cf6', in_review: '#3b82f6',
  approved: '#22c55e', rejected: '#ef4444', revision_requested: '#f97316'
};

const InvitationStatusPanel = ({ projectId }) => {
  const [invitations, setInvitations] = useState([]);
  useEffect(() => {
    if (!projectId) return;
    getProjectInvitations(projectId).then(setInvitations).catch(() => {});
  }, [projectId]);

  if (invitations.length === 0) return null;

  return (
    <div className="prj-inv-panel">
      <div className="prj-section-subtitle" style={{ marginTop: 12 }}>Davet Durumlari</div>
      {invitations.map(inv => (
        <div key={inv.id} className="prj-inv-row">
          <span className="prj-inv-target">
            {inv.targetOrganization ? inv.targetOrganization.name : inv.targetEmail || 'Dis Kurum'}
          </span>
          <span className="prj-inv-status" style={{ color: INV_STATUS_COLORS[inv.status] || '#888' }}>
            {INV_STATUS_LABELS[inv.status] || inv.status}
          </span>
        </div>
      ))}
    </div>
  );
};

const DRAFT_STORAGE_KEY = 'projabze_new_project_draft';

const Projects = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [user, setUser] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [creatingNew, setCreatingNew] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [theme] = useState(() => localStorage.getItem('projabze_theme') || 'dark');
  const [checkedIds, setCheckedIds] = useState(new Set());

  // Editable form state
  const [editName, setEditName] = useState('');
  const [editMeta, setEditMeta] = useState({});
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  // Pending files for new project (buffered locally before project exists)
  const [pendingFiles, setPendingFiles] = useState({});

  // Accordion state
  const [openSections, setOpenSections] = useState({ personal: true, genel: true, adres: false, kunye: false, muellifBilgi: false, paydaslar: false, sistem: false });
  const toggleSection = (key) => setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));

  // Atanan elektrik müellifinin profili — Müellif Bilgileri bölümünde gösterilir
  // ve boş Künye alanlarına otomatik aktarılır.
  const [muellifProfile, setMuellifProfile] = useState(null);
  const [muellifProfileLoading, setMuellifProfileLoading] = useState(false);

  // elektrikMuellifMemberId değişince /api/social/users/:id/profile çek
  useEffect(() => {
    const memberId = editMeta?.elektrikMuellifMemberId;
    if (!memberId) { setMuellifProfile(null); return; }
    const token = localStorage.getItem('projabze_token');
    if (!token) return;
    let cancelled = false;
    setMuellifProfileLoading(true);
    fetch(`${API_URL}/api/social/users/${memberId}/profile`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (!cancelled) setMuellifProfile(data || null); })
      .catch(() => { if (!cancelled) setMuellifProfile(null); })
      .finally(() => { if (!cancelled) setMuellifProfileLoading(false); });
    return () => { cancelled = true; };
  }, [editMeta?.elektrikMuellifMemberId]);

  // Müellif profili geldiğinde boş Künye alanlarını otomatik doldur
  useEffect(() => {
    if (!muellifProfile?.profile) return;
    const p = muellifProfile.profile;
    setEditMeta(prev => {
      const next = { ...prev };
      let changed = false;
      const fillIfEmpty = (key, value) => {
        if (!next[key] && value) { next[key] = value; changed = true; }
      };
      fillIfEmpty('authorChamberNo', p.chamberNo);
      fillIfEmpty('authorDiplomaNo', p.diplomaNo);
      fillIfEmpty('authorTaxRegNo',  p.vergiNo || p.buroTescilNo);
      fillIfEmpty('authorTaxOffice', p.vergiDairesi);
      return changed ? next : prev;
    });
  }, [muellifProfile]);
  const [filesOpen, setFilesOpen] = useState(true);
  const [listCollapsed, setListCollapsed] = useState(false);
  const [rightCollapsed, setRightCollapsed] = useState(false);
  const [previewHeight, setPreviewHeight] = useState(280);
  const [previewKey, setPreviewKey] = useState(0);
  const previewResizing = useRef(false);
  const previewStartY = useRef(0);
  const previewStartH = useRef(280);

  // File manager state
  const [attachments, setAttachments] = useState({});
  const [dragOverCat, setDragOverCat] = useState(null);
  const [uploadingCat, setUploadingCat] = useState(null);
  const fileInputRefs = useRef({});

  // Firms data (keyed by type)
  const [firmsMap, setFirmsMap] = useState({});

  // Team selection for stakeholders
  const [teamsList, setTeamsList] = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(false);

  // Detail tab: 'info' | 'chat' | 'notes'
  const [detailTab, setDetailTab] = useState('info');

  // Notes tab state
  const [notesList, setNotesList] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [hiddenNoteAuthors, setHiddenNoteAuthors] = useState(new Set());
  const [hiddenNoteSheets, setHiddenNoteSheets] = useState(new Set()); // tabId/'__general__' kapsayan set
  // Yanıt input'ları: { [noteId]: { text, sending } }
  const [replyDrafts, setReplyDrafts] = useState({});
  const setReplyDraft = (noteId, patch) => setReplyDrafts(prev => ({ ...prev, [noteId]: { ...(prev[noteId] || {}), ...patch } }));

  // Chat state
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [chatSending, setChatSending] = useState(false);
  const [chatTypers, setChatTypers] = useState([]);
  const [chatParticipants, setChatParticipants] = useState([]);
  // chatView: 'group' | dmUserId  — "group" = tüm paydaşlar, userId = o paydaşla DM
  const [chatView, setChatView] = useState('group');
  const chatEndRef = useRef(null);
  const chatFileRef = useRef(null);
  const chatPollRef = useRef(null);
  const typingTimerRef = useRef(null);

  // Preview
  const [previewFile, setPreviewFile] = useState(null);     // File for JbzViewer
  const [previewDxf, setPreviewDxf] = useState(null);       // parsed dxfData for DxfViewer
  const [previewPdf, setPreviewPdf] = useState(null); // File object for PDF preview via DocPreview
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null); // error string when preview fails
  const [pickMode, setPickMode] = useState(null); // null or field key string
  const [previewFileInfo, setPreviewFileInfo] = useState(null); // { cat, storedName } for active file highlight
  // Notlar sekmesinden bir nota tıklandığında JbzViewer'ı o noktaya odaklamak için.
  // ts: her tıklamada artar; tabId/point: notun konumu.
  const [viewerZoomTarget, setViewerZoomTarget] = useState(null);

  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      const list = await listProjects();
      setProjects(list);
    } catch (err) {
      console.error('Proje listesi alinamadi:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // ─── Pending stakeholder invitations (kullanıcıya gelen davetler) ───
  const [pendingInvites, setPendingInvites] = useState([]);
  const fetchPendingInvites = useCallback(async () => {
    try {
      const token = localStorage.getItem('projabze_token');
      const res = await fetch(`${API_URL}/api/project-invitations/inbox/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      setPendingInvites((data.invitations || []).filter(inv => inv.status === 'pending'));
    } catch (_) { /* ignore */ }
  }, []);

  const respondToInvitation = useCallback(async (invId, status) => {
    try {
      const token = localStorage.getItem('projabze_token');
      const res = await fetch(`${API_URL}/api/project-invitations/${invId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) return;
      setPendingInvites(prev => prev.filter(i => i.id !== invId));
      if (status === 'approved') fetchProjects();
    } catch (_) { /* ignore */ }
  }, [fetchProjects]);

  const fetchFirms = useCallback(async () => {
    try {
      const [all, distCompanies] = await Promise.all([listFirms(), getAllDistributionCompanies()]);
      const map = {};
      for (const f of FIRM_FIELDS) map[f.type] = [];
      for (const firm of all) {
        if (map[firm.type]) map[firm.type].push(firm);
      }
      // EPDK dağıtım şirketlerini 'elektrik_dagitim' grubuna ekle (dist: prefix ile)
      const distFirms = distCompanies.map(c => ({
        id: `dist:${c.id}`,
        name: c.not ? `${c.name} (${c.not})` : c.name,
        type: 'elektrik_dagitim',
        iller: c.iller || [],
      }));
      map['elektrik_dagitim'] = [...(map['elektrik_dagitim'] || []), ...distFirms];
      setFirmsMap(map);
    } catch (err) {
      console.error('Firma listesi alinamadi:', err);
    }
  }, []);

  const fetchTeamsList = useCallback(async () => {
    setTeamsLoading(true);
    try {
      const token = localStorage.getItem('projabze_token');
      const res = await fetch(`${API_URL}/api/teams`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setTeamsList(data.teams || []);
      }
    } catch { /* ignore */ }
    setTeamsLoading(false);
  }, []);

  const applyTeamToStakeholders = useCallback((teamId) => {
    const team = teamsList.find(t => t.id === teamId);
    if (!team) return;
    const accepted = (team.members || []).filter(m => m.status === 'accepted');
    const updates = {};
    // Kişi rolleri: malSahibi, muteahhit, mimariMuellif, elektrikMuellif, mekanikMuellif, kontrolMuhendis
    for (const pf of PERSON_FIELDS) {
      const member = accepted.find(m => m.role === pf.key);
      if (member) {
        updates[pf.key] = `${member.firstName || ''} ${member.lastName || ''}`.trim();
        updates[pf.key + 'Type'] = 'member';
        updates[pf.key + 'MemberId'] = member.userId;
        updates[pf.key + 'ExternalId'] = '';
      }
    }
    if (Object.keys(updates).length > 0) {
      setEditMeta(prev => ({ ...prev, ...updates }));
      setIsDirty(true);
    }
  }, [teamsList]);

  // URL builder — group veya DM endpoint'ini seçer
  const chatEndpoint = useCallback((projectId, view, suffix = 'messages') => {
    if (view && view !== 'group') {
      return `${API_URL}/api/project-chat/${projectId}/dm/${view}/${suffix}`;
    }
    return `${API_URL}/api/project-chat/${projectId}/${suffix}`;
  }, []);

  const fetchChatParticipants = useCallback(async (projectId) => {
    const token = localStorage.getItem('projabze_token');
    if (!token || !projectId) return;
    try {
      const res = await fetch(`${API_URL}/api/project-chat/${projectId}/participants`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setChatParticipants(data.participants || []);
      }
    } catch (_) { /* ignore */ }
  }, []);

  const fetchChatMessages = useCallback(async (projectId, view) => {
    const token = localStorage.getItem('projabze_token');
    if (!token || !projectId) return;
    try {
      setChatLoading(true);
      const res = await fetch(chatEndpoint(projectId, view), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setChatMessages(data.messages || []);
      }
    } catch (err) {
      console.error('Chat fetch error:', err);
    } finally {
      setChatLoading(false);
    }
  }, [chatEndpoint]);

  const sendChatMessage = async (projectId, body, view) => {
    const token = localStorage.getItem('projabze_token');
    if (!token || !body.trim()) return;
    try {
      setChatSending(true);
      const res = await fetch(chatEndpoint(projectId, view), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ body }),
      });
      if (res.ok) {
        const data = await res.json();
        setChatMessages(prev => [...prev, data.message]);
        setChatInput('');
      }
    } catch (err) {
      console.error('Chat send error:', err);
    } finally {
      setChatSending(false);
    }
  };

  const uploadChatFile = async (projectId, file, body, view) => {
    const token = localStorage.getItem('projabze_token');
    if (!token) return;
    try {
      setChatSending(true);
      const fd = new FormData();
      fd.append('file', file);
      if (body) fd.append('body', body);
      const res = await fetch(chatEndpoint(projectId, view, 'upload'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (res.ok) {
        const data = await res.json();
        setChatMessages(prev => [...prev, data.message]);
        setChatInput('');
      }
    } catch (err) {
      console.error('Chat upload error:', err);
    } finally {
      setChatSending(false);
    }
  };

  // Send typing signal (debounced — only fires once per 2s while user types)
  const sendTypingSignal = useCallback((projectId) => {
    if (typingTimerRef.current) return; // Already signaled recently
    const token = localStorage.getItem('projabze_token');
    if (!token || !projectId) return;
    fetch(`${API_URL}/api/project-chat/${projectId}/typing`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}` },
    }).catch(() => {});
    typingTimerRef.current = setTimeout(() => { typingTimerRef.current = null; }, 2000);
  }, []);

  // Poll typing indicators
  const fetchTyping = useCallback(async (projectId) => {
    const token = localStorage.getItem('projabze_token');
    if (!token || !projectId) return;
    try {
      const res = await fetch(`${API_URL}/api/project-chat/${projectId}/typing`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setChatTypers(data.typing || []);
      }
    } catch { /* ignore */ }
  }, []);

  // Auto-select project from URL param (e.g. /projects?id=xxx)
  useEffect(() => {
    const urlId = searchParams.get('id');
    if (!urlId) return;
    if (projects.length > 0) {
      const found = projects.find(p => p.id === urlId);
      if (found) {
        setSelectedId(urlId);
      }
      setSearchParams({}, { replace: true }); // Clean URL
    }
  }, [projects, searchParams, setSearchParams, location.search, location.state]);

  // Highlight invitation from URL param (e.g. /projects?invitation=xxx)
  const [highlightInvitationId, setHighlightInvitationId] = useState(null);
  useEffect(() => {
    const invId = searchParams.get('invitation');
    if (!invId) return;
    setHighlightInvitationId(invId);
    fetchPendingInvites();
    setSearchParams({}, { replace: true });
    setTimeout(() => {
      const el = document.querySelector(`[data-invite-id="${invId}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 300);
    const t = setTimeout(() => setHighlightInvitationId(null), 4000);
    return () => clearTimeout(t);
  }, [searchParams, setSearchParams, fetchPendingInvites, location.search, location.state]);

  // Chat polling & auto-scroll
  useEffect(() => {
    if (detailTab === 'chat' && selectedId) {
      fetchChatParticipants(selectedId);
      fetchChatMessages(selectedId, chatView);
      if (chatView === 'group') fetchTyping(selectedId);
      const msgInterval = setInterval(() => fetchChatMessages(selectedId, chatView), 10000);
      const typingInterval = chatView === 'group'
        ? setInterval(() => fetchTyping(selectedId), 2000)
        : null;
      return () => {
        clearInterval(msgInterval);
        if (typingInterval) clearInterval(typingInterval);
      };
    }
    setChatTypers([]);
    return () => {};
  }, [detailTab, selectedId, chatView, fetchChatMessages, fetchTyping, fetchChatParticipants]);

  // Sohbet görünümü değiştiğinde mesajları ve input'u sıfırla
  useEffect(() => {
    setChatMessages([]);
    setChatInput('');
    setChatTypers([]);
  }, [chatView]);

  // Proje değişince default olarak grup görünümüne dön
  useEffect(() => {
    setChatView('group');
  }, [selectedId]);

  // ─── Callout notları: Notlar sekmesi için ───
  const submitReply = async (noteId) => {
    if (!selectedId) return;
    const draft = replyDrafts[noteId] || {};
    const text = (draft.text || '').trim();
    if (!text || draft.sending) return;
    setReplyDraft(noteId, { sending: true });
    try {
      const reply = await postNoteReply(selectedId, noteId, text);
      // Lokal state'e enjekte et — server-poll de güncel hali getirir
      setNotesList(prev => prev.map(n => n.id === noteId
        ? { ...n, replies: [...(n.replies || []), reply] }
        : n));
      setReplyDraft(noteId, { text: '', sending: false });
    } catch (e) {
      setReplyDraft(noteId, { sending: false });
    }
  };

  const removeReply = async (noteId, replyId) => {
    if (!selectedId) return;
    try {
      await deleteNoteReply(selectedId, noteId, replyId);
      setNotesList(prev => prev.map(n => n.id === noteId
        ? { ...n, replies: (n.replies || []).filter(r => r.id !== replyId) }
        : n));
    } catch (_) { /* ignore */ }
  };

  const fetchNotes = useCallback(async (projectId) => {
    const token = localStorage.getItem('projabze_token');
    if (!token || !projectId) return;
    try {
      setNotesLoading(true);
      const res = await fetch(`${API_URL}/api/projects/${projectId}/notes`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setNotesList(data.notes || []);
      }
    } catch (_) { /* ignore */ }
    finally { setNotesLoading(false); }
  }, []);

  useEffect(() => {
    if (detailTab === 'notes' && selectedId) {
      fetchChatParticipants(selectedId);
      fetchNotes(selectedId);
      const interval = setInterval(() => fetchNotes(selectedId), 5000);
      return () => clearInterval(interval);
    }
    return () => {};
  }, [detailTab, selectedId, fetchNotes, fetchChatParticipants]);

  // Paydaş filtresi proje değişince temizlenir
  useEffect(() => {
    setHiddenNoteAuthors(new Set());
  }, [selectedId]);

  useEffect(() => {
    if (chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  useEffect(() => {
    const stored = localStorage.getItem('projabze_user');
    if (stored) {
      setUser(JSON.parse(stored));
      fetchProjects();
      fetchFirms();
      fetchPendingInvites();
      // Restore draft if exists
      try {
        const draft = localStorage.getItem(DRAFT_STORAGE_KEY);
        if (draft) {
          const { name, meta } = JSON.parse(draft);
          if (name || (meta && Object.keys(meta).some(k => meta[k]))) {
            setCreatingNew(true);
            setEditName(name || '');
            setEditMeta(meta || {});
          }
        }
      } catch { /* ignore corrupt draft */ }
    } else {
      navigate('/');
    }
  }, [navigate, fetchProjects, fetchFirms, fetchPendingInvites]);

  const isNewMode = creatingNew && !selectedId;

  // Auto-save draft to localStorage while in new project mode
  useEffect(() => {
    if (!isNewMode) return;
    const hasData = editName || Object.keys(editMeta).some(k => editMeta[k]);
    if (hasData) {
      localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify({ name: editName, meta: editMeta }));
    } else {
      localStorage.removeItem(DRAFT_STORAGE_KEY);
    }
  }, [isNewMode, editName, editMeta]);

  const startNewProject = () => {
    setSelectedId(null);
    setCreatingNew(true);
    localStorage.removeItem(DRAFT_STORAGE_KEY);
    setEditName('');
    // Proje sahibinin sektör rolüne (+ disipline) denk gelen paydaş alanını
    // kendi adıyla otomatik doldur. Örn. user.sectorRole='muellif' +
    // user.discipline='disc-electrical' → elektrikMuellif satırı user'a atanır.
    const ownerKey = userToPersonFieldKey({
      sectorRole: user?.sectorRole || '',
      discipline: user?.discipline || '',
    });
    if (ownerKey && user?.id) {
      const dk = personFieldDataKeys(ownerKey);
      const ownerName = `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || '';
      setEditMeta({
        [dk.name]: ownerName,
        [dk.type]: 'member',
        [dk.memberId]: user.id,
        [dk.externalId]: '',
      });
    } else {
      setEditMeta({});
    }
    setIsDirty(false);
    setPendingFiles({});
    setPreviewFile(null);
    setPreviewDxf(null);
    setPreviewPdf(null);
    setPreviewFileInfo(null);
    setAttachments({});
  };

  // Sync form state when selection changes
  const selected = projects.find(p => p.id === selectedId);
  const isShared = selected && selected._shared;
  const isDraft = isNewMode || (selected && !selected.currentVersion);

  // Bir kategoriyi indirme yetkisi (UI seviyesinde). JBZ için: yalnızca proje
  // sahibi veya disiplinin müellifi (şimdilik elektrikMuellif). Diğerleri:
  // tüm paydaşlar indirebilir (mevcut davranış).
  const JBZ_DOWNLOADABLE_VIEWERS = new Set(['elektrikMuellif']);
  const canDownloadCat = (cat) => {
    if (!isShared) return true; // proje sahibi
    if (cat.key === 'jbz') {
      return selected && JBZ_DOWNLOADABLE_VIEWERS.has(selected._viewerKey);
    }
    // JBZ dışındaki kategoriler için mevcut davranış: paydaşlar indirebilir.
    return true;
  };

  // Editör'de açma yetkisi:
  //  - Sahip: kendi projesini her zaman açabilir (müellif ataması şart değil).
  //  - Paylaşılmış proje: yalnızca elektrik müellifi olarak atanan paydaş açabilir.
  const canOpenInEditor = !!selected && (
    isShared
      ? selected._viewerKey === 'elektrikMuellif'
      : true
  );
  useEffect(() => {
    if (selected) {
      setEditName(selected.name || '');
      setEditMeta(selected.metadata || {});
      setIsDirty(false);
      setCreatingNew(false);
    }
  }, [selectedId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load attachments when project selected
  const fetchAttachments = useCallback(async (pid) => {
    try {
      const data = await listAttachments(pid);
      setAttachments(data);
    } catch { setAttachments({}); }
  }, []);

  useEffect(() => {
    if (selectedId) fetchAttachments(selectedId);
    else setAttachments({});
  }, [selectedId, fetchAttachments]);

  // Bu projedeki paydaş share linklerini yükle ve editMeta.stakeholderAccess
  // alanındaki shareToken'ları DB ile senkronla.
  useEffect(() => {
    if (!selectedId) return;
    let cancelled = false;
    (async () => {
      try {
        const shares = await getStakeholderShares(selectedId);
        if (cancelled) return;
        if (!Array.isArray(shares) || shares.length === 0) return;
        setEditMeta(prev => {
          const sa = { ...(prev.stakeholderAccess || {}) };
          for (const s of shares) {
            if (!s.stakeholderKey) continue;
            sa[s.stakeholderKey] = { ...(sa[s.stakeholderKey] || {}), shareToken: s.token };
          }
          return { ...prev, stakeholderAccess: sa };
        });
      } catch { /* sessiz: backend hazır değilse */ }
    })();
    return () => { cancelled = true; };
  }, [selectedId]);

  // Poll shared project data so stakeholders see owner's changes in real-time
  useEffect(() => {
    if (!isShared || !selectedId) return;
    const poll = setInterval(async () => {
      try {
        const fresh = await getProject(selectedId);
        if (fresh) {
          setProjects(prev => prev.map(p => p.id === selectedId ? { ...p, ...fresh, _shared: true } : p));
          setEditName(fresh.name || '');
          setEditMeta(fresh.metadata || {});
        }
      } catch { /* ignore */ }
    }, 8000);
    return () => clearInterval(poll);
  }, [isShared, selectedId]);

  // Dosya yükleme yetkisi: proje sahibi veya ilgili müellif
  const canUploadToCategory = useCallback((catKey) => {
    if (!user) return false;
    // Yeni proje modunda herkes yükleyebilir (kendi projesi)
    if (!selected) return true;
    // Proje sahibi her kategoriye yükleyebilir
    if (selected.userId === user.id) return true;
    // Müellif yetkisi: metadata'daki MemberId ile kontrol
    const cat = FILE_CATEGORIES.find(c => c.key === catKey);
    if (!cat?.uploadRole) return false;
    const meta = selected.metadata || {};
    return meta[cat.uploadRole + 'MemberId'] === user.id;
  }, [user, selected]);

  // Dosya kategorileri yatay kaydırma — fare ile sürükleme
  const fmScrollRef = useRef(null);
  const handleFmScrollMouseDown = useCallback((e) => {
    const el = fmScrollRef.current;
    if (!el) return;
    // Kartların içindeki etkileşimlere karışma (dosya linki, silme butonu vb.)
    if (e.target.closest('.prj-fm-file, .prj-fm-remove, button, a, input')) return;
    if (e.button !== 0) return;
    const startX = e.clientX;
    const startScroll = el.scrollLeft;
    let moved = false;
    el.classList.add('dragging');
    const onMove = (ev) => {
      const dx = ev.clientX - startX;
      if (Math.abs(dx) > 3) moved = true;
      el.scrollLeft = startScroll - dx;
    };
    const onUp = (ev) => {
      el.classList.remove('dragging');
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      if (moved) {
        // Sürükleme sonrası ortaya çıkan click'i yut
        const swallow = (clickEv) => { clickEv.stopPropagation(); clickEv.preventDefault(); document.removeEventListener('click', swallow, true); };
        document.addEventListener('click', swallow, true);
      }
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, []);

  // Ön izleme yükseklik sürükleme
  const handlePreviewResizeStart = useCallback((e) => {
    e.preventDefault();
    previewResizing.current = true;
    previewStartY.current = e.clientY;
    previewStartH.current = previewHeight;
    const onMove = (ev) => {
      if (!previewResizing.current) return;
      const delta = ev.clientY - previewStartY.current;
      setPreviewHeight(Math.max(280, previewStartH.current + delta));
    };
    const onUp = () => {
      previewResizing.current = false;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      setPreviewKey(k => k + 1);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [previewHeight]);

  const handleFileDrop = useCallback(async (cat, files) => {
    if (!files.length) return;
    // New mode: buffer files locally
    if (!selectedId) {
      setPendingFiles(prev => ({
        ...prev,
        [cat]: [...(prev[cat] || []), ...files],
      }));
      setIsDirty(true);
      return;
    }
    setUploadingCat(cat);
    try {
      await uploadAttachments(selectedId, cat, files);
      await fetchAttachments(selectedId);
    } catch (err) { console.error('Upload hatasi:', err); alert('Dosya yukleme hatasi: ' + (err.message || err)); }
    finally { setUploadingCat(null); }
  }, [selectedId, fetchAttachments]);

  const handleFileDelete = useCallback(async (cat, storedName) => {
    // New mode: remove from pending
    if (!selectedId) {
      setPendingFiles(prev => ({
        ...prev,
        [cat]: (prev[cat] || []).filter(f => f.name !== storedName),
      }));
      return;
    }
    try {
      await deleteAttachment(selectedId, cat, storedName);
      await fetchAttachments(selectedId);
    } catch (err) { console.error('Silme hatasi:', err); }
  }, [selectedId, fetchAttachments]);

  // Generate preview from first pending file in new mode
  useEffect(() => {
    if (!isNewMode) return;
    // Find the first file across all categories
    let firstFile = null;
    for (const cat of FILE_CATEGORIES) {
      const files = pendingFiles[cat.key];
      if (files && files.length > 0) { firstFile = files[0]; break; }
    }
    if (!firstFile) { setPreviewFile(null); setPreviewDxf(null); setPreviewPdf(null); return; }

    let cancelled = false;
    const name = firstFile.name.toLowerCase();

    if (name.endsWith('.jbz')) {
      setPreviewDxf(null); setPreviewPdf(null);
      setPreviewFile(firstFile);
    } else if (name.endsWith('.pdf')) {
      setPreviewDxf(null); setPreviewFile(null);
      setPreviewPdf(firstFile);
    } else if (name.endsWith('.dxf') || name.endsWith('.dwg')) {
      setPreviewFile(null); setPreviewPdf(null);
      (async () => {
        try {
          setPreviewLoading(true);
          let text;
          if (name.endsWith('.dwg')) {
            text = await getDxfTextFromDwg(firstFile);
          } else {
            text = await firstFile.text();
          }
          const DxfParser = (await import('dxf-parser')).default;
          const parser = new DxfParser();
          const dxf = parser.parseSync(text);
          if (!cancelled) setPreviewDxf(dxf);
        } catch (err) {
          console.error('DXF parse error:', err);
          if (!cancelled) setPreviewDxf(null);
        } finally {
          if (!cancelled) setPreviewLoading(false);
        }
      })();
    }
    return () => { cancelled = true; };
  }, [pendingFiles, isNewMode]);

  // Load JBZ preview when saved project selected.
  // Öncelik: 1) Proje Dosyaları > JBZ Dosyaları'ndaki ilk JBZ attachment
  //          2) Editor'ün son kaydedilmiş versiyonu (loadProject)
  // IMPORTANT: depend on selectedId + currentVersion only — not the whole `projects` array,
  // otherwise background polling re-fetches the JBZ every 8s and resets the viewer.
  const selectedCurrentVersion = projects.find(p => p.id === selectedId)?.currentVersion || 0;
  const selectedShared = !!projects.find(p => p.id === selectedId)?._shared;
  useEffect(() => {
    setPreviewFileInfo(null);
    setPreviewPdf(null);
    setPreviewError(null);
    if (!selectedId) { setPreviewFile(null); setPreviewDxf(null); return; }
    setPreviewDxf(null);

    // 1) JBZ attachment varsa onu yükle (sahip ve paydaş kullanıcılar için).
    let firstJbz = null;
    let firstJbzCat = null;
    for (const cat of FILE_CATEGORIES) {
      const files = attachments[cat.key];
      if (files && files.length > 0) {
        const jbzFile = files.find(f => /\.jbz$/i.test(f.name));
        if (jbzFile) { firstJbz = jbzFile; firstJbzCat = cat.key; break; }
      }
    }

    let cancelled = false;
    (async () => {
      try {
        setPreviewLoading(true);
        if (firstJbz) {
          const token = localStorage.getItem('projabze_token');
          const res = await fetch(`${API_URL}/api/projects/${selectedId}/attachments/${firstJbzCat}/${firstJbz.storedName}`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          });
          if (!res.ok) throw new Error(`JBZ fetch ${res.status}`);
          const buf = await res.arrayBuffer();
          if (!cancelled) {
            setPreviewFile(new File([buf], firstJbz.name));
            setPreviewFileInfo({ cat: firstJbzCat, storedName: firstJbz.storedName });
          }
        } else if (selectedCurrentVersion) {
          const buf = await loadProject(selectedId);
          if (!cancelled) {
            setPreviewFile(new File([buf], 'preview.jbz'));
          }
        } else {
          if (!cancelled) setPreviewFile(null);
        }
      } catch (err) {
        console.warn('JBZ preview load failed:', err?.message || err);
        if (!cancelled) setPreviewFile(null);
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedId, selectedCurrentVersion, selectedShared, attachments]);

  // Generate preview for draft projects from uploaded DXF attachments
  useEffect(() => {
    if (!selectedId || previewDxf || previewFile || previewPdf) return;
    const sel = projects.find(p => p.id === selectedId);
    if (!sel || sel.currentVersion > 0) return;
    // JBZ attachment'ı yukarıdaki ana effect zaten ele aldı; burada sadece
    // DXF/DWG fallback (draft modda).
    let firstDxf = null;
    let firstDxfCat = null;
    for (const cat of FILE_CATEGORIES) {
      const files = attachments[cat.key];
      if (files && files.length > 0) {
        const cadFile = files.find(f => /\.(dxf|dwg)$/i.test(f.name));
        if (cadFile) { firstDxf = cadFile; firstDxfCat = cat.key; break; }
      }
    }
    if (!firstDxf) return;
    const isDwg = firstDxf.name.toLowerCase().endsWith('.dwg');
    let cancelled = false;
    (async () => {
      try {
        setPreviewLoading(true);
        const token = localStorage.getItem('projabze_token');
        const res = await fetch(`${API_URL}/api/projects/${selectedId}/attachments/${firstDxfCat}/${firstDxf.storedName}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) throw new Error('Fetch failed');
        let text;
        if (isDwg) {
          const dwgBlob = await res.blob();
          const dwgFile = new File([dwgBlob], firstDxf.name);
          text = await getDxfTextFromDwg(dwgFile);
        } else {
          text = await res.text();
        }
        const DxfParser = (await import('dxf-parser')).default;
        const parser = new DxfParser();
        const dxf = parser.parseSync(text);
        if (!cancelled) setPreviewDxf(dxf);
      } catch (err) {
        console.error('Draft CAD preview error:', err);
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedId, projects, attachments, previewDxf, previewFile, previewPdf]);

  // Clear all preview states helper
  const clearPreview = () => {
    setPreviewDxf(null);
    setPreviewFile(null);
    setPreviewPdf(null);
    setPreviewError(null);
  };

  // Load preview for a specific file when its name is clicked
  const handleFilePreview = useCallback(async (catKey, fileObj) => {
    setPreviewFileInfo({ cat: catKey, storedName: fileObj.storedName || fileObj.name });
    const name = (fileObj.name || fileObj.storedName).toLowerCase();

    if (isNewMode) {
      // New mode: fileObj is a local File object from pendingFiles
      const localFile = (pendingFiles[catKey] || []).find(f => f.name === fileObj.name);
      if (!localFile) return;

      if (name.endsWith('.jbz')) {
        clearPreview();
        setPreviewFile(localFile);
      } else if (name.endsWith('.pdf')) {
        clearPreview();
        setPreviewPdf(localFile);
      } else if (name.endsWith('.dxf') || name.endsWith('.dwg')) {
        try {
          setPreviewError(null);
          setPreviewLoading(true);
          let text;
          if (name.endsWith('.dwg')) {
            text = await getDxfTextFromDwg(localFile);
          } else {
            text = await localFile.text();
          }
          const DxfParser = (await import('dxf-parser')).default;
          const parser = new DxfParser();
          const dxf = parser.parseSync(text);
          clearPreview();
          setPreviewDxf(dxf);
        } catch (err) {
          console.error('DXF/DWG parse error:', err);
          setPreviewError(err.message || 'DWG dönüştürme başarısız');
          setPreviewFileInfo(null);
        } finally {
          setPreviewLoading(false);
        }
      }
    } else {
      // Saved project mode: fetch from server
      const token = localStorage.getItem('projabze_token');
      const url = `${API_URL}/api/projects/${selectedId}/attachments/${catKey}/${fileObj.storedName}`;
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      if (name.endsWith('.jbz')) {
        clearPreview();
        try {
          setPreviewLoading(true);
          const res = await fetch(url, { headers });
          if (!res.ok) {
            const errText = res.status === 404 ? 'Dosya sunucuda bulunamadi' : `Sunucu hatasi (${res.status})`;
            throw new Error(errText);
          }
          const buf = await res.arrayBuffer();
          setPreviewFile(new File([buf], fileObj.name));
        } catch (err) {
          console.error('JBZ preview error:', err);
          setPreviewError(err?.message || 'JBZ yuklenemedi');
        } finally {
          setPreviewLoading(false);
        }
      } else if (name.endsWith('.pdf')) {
        clearPreview();
        try {
          setPreviewLoading(true);
          const res = await fetch(url, { headers });
          if (!res.ok) throw new Error('Fetch failed');
          const blob = await res.blob();
          setPreviewPdf(new File([blob], fileObj.name, { type: 'application/pdf' }));
        } catch (err) {
          console.error('PDF preview error:', err);
        } finally {
          setPreviewLoading(false);
        }
      } else if (name.endsWith('.dxf') || name.endsWith('.dwg')) {
        try {
          setPreviewError(null);
          setPreviewLoading(true);
          const res = await fetch(url, { headers });
          if (!res.ok) throw new Error('Fetch failed');
          let text;
          if (name.endsWith('.dwg')) {
            const dwgBlob = await res.blob();
            const dwgFile = new File([dwgBlob], fileObj.name);
            text = await getDxfTextFromDwg(dwgFile);
          } else {
            text = await res.text();
          }
          const DxfParser = (await import('dxf-parser')).default;
          const parser = new DxfParser();
          const dxf = parser.parseSync(text);
          clearPreview();
          setPreviewDxf(dxf);
        } catch (err) {
          console.error('DXF/DWG preview error:', err);
          setPreviewError(err.message || 'DWG dönüştürme başarısız');
          setPreviewFileInfo(null);
        } finally {
          setPreviewLoading(false);
        }
      }
    }
  }, [isNewMode, pendingFiles, selectedId]);

  // Explicit save
  const handleSave = useCallback(async () => {
    if (saving) return;

    // --- NEW PROJECT MODE ---
    if (isNewMode) {
      if (!editName.trim()) { alert('Proje adi zorunludur.'); return; }
      try {
        setSaving(true);
        // 1. Create project
        const project = await createProject(editName.trim());
        // 2. Update metadata
        const updated = await updateProject(project.id, { name: editName.trim(), metadata: editMeta });
        // 3. Upload pending files
        for (const [cat, files] of Object.entries(pendingFiles)) {
          if (files.length) await uploadAttachments(project.id, cat, files);
        }
        // 4. Clear draft & refresh
        localStorage.removeItem(DRAFT_STORAGE_KEY);
        await fetchProjects();
        setPendingFiles({});
        setCreatingNew(false);
        setSelectedId(updated.id);
        setIsDirty(false);
      } catch (err) {
        console.error('Proje olusturma hatasi:', err);
        alert('Proje olusturma hatasi: ' + (err.message || err));
      } finally {
        setSaving(false);
      }
      return;
    }

    // --- EXISTING PROJECT MODE ---
    if (!selected) return;
    try {
      setSaving(true);
      const updated = await updateProject(selected.id, { name: editName, metadata: editMeta });
      setProjects(prev => prev.map(p => p.id === selected.id ? updated : p));
      setIsDirty(false);
    } catch (err) {
      console.error('Kaydetme hatasi:', err);
      alert('Kaydetme hatasi: ' + (err.message || err));
    } finally {
      setSaving(false);
    }
  }, [selected, isNewMode, editName, editMeta, saving, pendingFiles, fetchProjects]);

  const handleNameChange = (val) => {
    if (isShared) return;
    setEditName(val);
    setIsDirty(true);
  };

  const handleTextPick = useCallback((text) => {
    if (pickMode === 'name') {
      setEditName(text);
    } else if (pickMode) {
      setEditMeta(prev => ({ ...prev, [pickMode]: text }));
    }
    setPickMode(null);
    setIsDirty(true);
  }, [pickMode]);

  const handleMetaChange = (key, val) => {
    if (isShared) return;
    setEditMeta(prev => ({ ...prev, [key]: val }));
    setIsDirty(true);
  };

  // ─── Özel paydaşlar (Kişiler altında "+ Ekle" ile eklenenler) ───────
  // editMeta.customStakeholders = [{ id, label, name, type, memberId, externalId }]
  const addCustomStakeholder = () => {
    if (isShared) return;
    const id = `custom-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
    setEditMeta(prev => ({
      ...prev,
      customStakeholders: [
        ...(prev.customStakeholders || []),
        { id, label: '', name: '', type: '', memberId: '', externalId: '' },
      ],
    }));
    setIsDirty(true);
  };
  const updateCustomStakeholder = (id, patch) => {
    if (isShared) return;
    setEditMeta(prev => ({
      ...prev,
      customStakeholders: (prev.customStakeholders || []).map(cs =>
        cs.id === id ? { ...cs, ...patch } : cs
      ),
    }));
    setIsDirty(true);
  };
  const removeCustomStakeholder = (id) => {
    if (isShared) return;
    setEditMeta(prev => {
      const sa = { ...(prev.stakeholderAccess || {}) };
      delete sa[id];
      return {
        ...prev,
        customStakeholders: (prev.customStakeholders || []).filter(cs => cs.id !== id),
        stakeholderAccess: sa,
      };
    });
    setIsDirty(true);
  };

  // Paydaş erişim verisini editMeta.stakeholderAccess[key] altında günceller.
  // patch: { templateId?, override?, shareToken? } — verilen alanları merge eder.
  const updateStakeholderAccess = (stakeholderKey, patch) => {
    if (isShared) return;
    setEditMeta(prev => {
      const cur = (prev.stakeholderAccess || {})[stakeholderKey] || {};
      return {
        ...prev,
        stakeholderAccess: {
          ...(prev.stakeholderAccess || {}),
          [stakeholderKey]: { ...cur, ...patch },
        },
      };
    });
    // shareToken backend tarafında üretiliyor — proje zaten var; ama
    // erişim ayarı (templateId/override) lokal değişiklik, dirty işaretle.
    if ('templateId' in patch || 'override' in patch) setIsDirty(true);
  };

  const handleCreateFirm = async (name, type) => {
    try {
      const { firm } = await createFirm({ name, type });
      // Refresh firms list
      setFirmsMap(prev => ({
        ...prev,
        [type]: [...(prev[type] || []), firm].sort((a, b) => a.name.localeCompare(b.name, 'tr')),
      }));
      return firm;
    } catch (err) {
      console.error('Firma olusturma hatasi:', err);
      return null;
    }
  };

  if (!user) return null;

  const hasPreview = !!(previewDxf || previewFile || previewPdf);
  const PickBtn = ({ field }) => hasPreview ? (
    <button
      className={`prj-pick-btn ${pickMode === field ? 'active' : ''}`}
      onClick={() => setPickMode(p => p === field ? null : field)}
      title="Çizimden metin seç"
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3"/>
        <path d="M12 2v4M12 18v4M2 12h4M18 12h4"/>
      </svg>
    </button>
  ) : null;

  const filtered = projects.filter(p =>
    p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (p.metadata?.client || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (p.metadata?.city || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const handleDelete = async (id) => {
    const prj = projects.find(p => p.id === id);
    const name = prj?.name || prj?.metadata?.name || id;
    if (!window.confirm(`"${name}" projesini silmek istediğinizden emin misiniz?`)) return;
    try {
      await deleteProject(id);
      setProjects(prev => prev.filter(p => p.id !== id));
      if (selectedId === id) setSelectedId(null);
      window.dispatchEvent(new Event('projabze-storage-updated'));
    } catch (err) {
      alert('Silme hatasi: ' + (err.message || err));
    }
  };

  const handleBulkDelete = async () => {
    const ownChecked = [...checkedIds].filter(id => {
      const p = projects.find(q => q.id === id);
      return p && !p._shared;
    });
    if (ownChecked.length === 0) return;
    const names = ownChecked.map(id => projects.find(p => p.id === id)?.name || id).join('\n• ');
    if (!window.confirm(`${ownChecked.length} projeyi silmek istediğinizden emin misiniz?\n\n• ${names}`)) return;
    const errors = [];
    for (const id of ownChecked) {
      try {
        await deleteProject(id);
        setProjects(prev => prev.filter(p => p.id !== id));
        if (selectedId === id) setSelectedId(null);
      } catch (err) {
        errors.push(err.message || String(err));
      }
    }
    setCheckedIds(new Set());
    window.dispatchEvent(new Event('projabze-storage-updated'));
    if (errors.length) {
      alert(`${errors.length} proje silinemedi:\n${errors.slice(0, 5).join('\n')}`);
    }
    // Listeyi sunucudan yenile — optimistik UI güncellemesi yeterli olmayabilir
    await fetchProjects();
  };

  const handleLogout = () => {
    localStorage.removeItem('projabze_token');
    localStorage.removeItem('projabze_user');
    navigate('/');
  };

  // Resolve firm name for list display
  const getFirmName = (firmId) => {
    if (!firmId) return null;
    for (const firms of Object.values(firmsMap)) {
      const f = firms.find(x => x.id === firmId);
      if (f) return f.name;
    }
    return null;
  };

  return (
    <div className={`prj-page ${theme} ${rightCollapsed ? 'right-collapsed' : ''}`}>
      <Topbar user={user} onLogout={handleLogout} />
      <Sidebar activeId="projects" user={user} onLogout={handleLogout} />

      <div className={`prj-content ${listCollapsed ? 'list-collapsed' : ''}`}>
      {/* PROJECT LIST */}
      <div className={`prj-list-panel ${listCollapsed ? 'collapsed' : ''}`}>
        <div className="prj-list-header">
          <h2>Projelerim</h2>
          <div className="prj-list-header-actions">
            {checkedIds.size > 0 && (
              <button className="prj-bulk-delete-btn" onClick={handleBulkDelete}>
                Sil ({checkedIds.size})
              </button>
            )}
            <button className="prj-new-btn" onClick={startNewProject}>+ Yeni</button>
          </div>
        </div>

        <div className="prj-search-wrapper">
          <input
            className="prj-search"
            type="text"
            placeholder="Proje, müşteri veya şehir ara..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </div>

        {pendingInvites.length > 0 && (
          <div className="prj-invites">
            <div className="prj-invites-header">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
              Paydaş Davetleri ({pendingInvites.length})
            </div>
            {pendingInvites.map(inv => {
              const senderName = inv.sender ? `${inv.sender.firstName || ''} ${inv.sender.lastName || ''}`.trim() : '';
              const roleLabels = {
                malSahibiMemberId: 'Mal Sahibi',
                muteahhitMemberId: 'Müteahhit',
                mimariMuellifMemberId: 'Mimari Müellif',
                elektrikMuellifMemberId: 'Elektrik Müellifi',
                mekanikMuellifMemberId: 'Mekanik Müellifi',
                kontrolMuhendisMemberId: 'Kontrol Mühendisi',
                clientMemberId: 'Müşteri',
              };
              const roleLabel = roleLabels[inv.partyTypeId] || 'Paydaş';
              return (
                <div key={inv.id} data-invite-id={inv.id} className={`prj-invite-card ${highlightInvitationId === inv.id ? 'highlight' : ''}`}>
                  <div className="prj-invite-info">
                    <span className="prj-invite-project">{inv.project?.name || 'Proje'}</span>
                    <span className="prj-invite-meta">
                      <b>{senderName}</b> · <span className="prj-invite-role">{roleLabel}</span>
                    </span>
                  </div>
                  <div className="prj-invite-actions">
                    <button className="prj-invite-accept" onClick={() => respondToInvitation(inv.id, 'approved')}>Kabul</button>
                    <button className="prj-invite-reject" onClick={() => respondToInvitation(inv.id, 'rejected')}>Reddet</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="prj-list">
          {isNewMode && (
            <div className="prj-list-item selected">
              <div className="prj-item-top">
                <span className="prj-item-name">{editName || 'Yeni Proje'}</span>
              </div>
              <div className="prj-item-mid">
                <span className="prj-item-status draft">Taslak</span>
              </div>
            </div>
          )}
          {loading ? (
            <div className="prj-list-empty">Yükleniyor...</div>
          ) : filtered.length === 0 ? (
            <div className="prj-list-empty">
              {projects.length === 0
                ? 'Henuz projeniz yok. Editorde bir proje kaydedin.'
                : 'Eslesen proje bulunamadi.'}
            </div>
          ) : (
            filtered.map(p => (
              <div
                key={p.id}
                className={`prj-list-item ${selectedId === p.id ? 'selected' : ''} ${checkedIds.has(p.id) ? 'checked' : ''}`}
                onClick={() => {
                  if ((isDirty || isNewMode) && selectedId !== p.id) {
                    if (!window.confirm('Kaydedilmemis degisiklikler var. Devam etmek istiyor musunuz?')) return;
                  }
                  setCreatingNew(false);
                  setSelectedId(p.id);
                }}
              >
                {!p._shared && (
                  <div
                    className="prj-item-checkbox-wrap"
                    onClick={e => {
                      e.stopPropagation();
                      setCheckedIds(prev => {
                        const next = new Set(prev);
                        next.has(p.id) ? next.delete(p.id) : next.add(p.id);
                        return next;
                      });
                    }}
                  >
                    <input
                      type="checkbox"
                      className="prj-item-checkbox"
                      checked={checkedIds.has(p.id)}
                      onChange={() => {}}
                    />
                  </div>
                )}
                <div className="prj-item-content">
                <div className="prj-item-top">
                  <span className="prj-item-name">{p.name}</span>
                  <span className="prj-item-date">{formatDate(p.updatedAt)}</span>
                </div>
                {(() => {
                  const owner = p.owner;
                  const ownerName = owner ? `${owner.firstName || ''} ${owner.lastName || ''}`.trim() : '';
                  const ownerInitials = owner ? (`${owner.firstName?.[0] || ''}${owner.lastName?.[0] || ''}`.toUpperCase() || '?') : '';
                  const locParts = [
                    p.metadata?.district,
                    p.metadata?.ada && `Ada ${p.metadata.ada}`,
                    p.metadata?.parsel && `Parsel ${p.metadata.parsel}`,
                  ].filter(Boolean);
                  return (
                    <>
                      {owner && (
                        <div className="prj-item-owner-row">
                          {owner.avatar
                            ? <img className="prj-item-owner-avatar" src={API_URL + owner.avatar} alt="" />
                            : <span className="prj-item-owner-avatar prj-item-owner-initials">{ownerInitials}</span>
                          }
                          <span className="prj-item-role-label">Proje Sahibi:</span>
                          <span className="prj-item-owner-name" title={ownerName}>{ownerName || 'Bilinmiyor'}</span>
                        </div>
                      )}
                      {p._viewerRole && (
                        <div className="prj-item-viewer-row">
                          <span className="prj-item-role-label">Senin rolün:</span>
                          <span className="prj-item-viewer-role">{p._viewerRole}</span>
                        </div>
                      )}
                      {locParts.length > 0 && (
                        <div className="prj-item-loc">{locParts.join(' · ')}</div>
                      )}
                      <div className="prj-item-mid">
                        {p._shared && <span className="prj-item-shared-tag">Paylasilmis</span>}
                        <span className="prj-item-status active">
                          {p.currentVersion > 0 ? `v${p.currentVersion}` : 'Yeni'}
                        </span>
                        {p.metadata?.client && <span className="prj-item-type" title={p.metadata.client}>{p.metadata.client}</span>}
                        {p.metadata?.city && <span className="prj-item-type" title={p.metadata.city}>{p.metadata.city}</span>}
                      </div>
                    </>
                  );
                })()}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* DETAIL PANEL */}
      <div className="prj-detail-panel">
        {!listCollapsed && (
          <button className="prj-collapse-left-btn" onClick={() => setListCollapsed(true)} title="Listeyi daralt">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="11 17 6 12 11 7"/><polyline points="18 17 13 12 18 7"/></svg>
          </button>
        )}
        {listCollapsed && (
          <button className="prj-expand-btn" onClick={() => setListCollapsed(false)} title="Listeyi goster">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="13 17 18 12 13 7"/><polyline points="6 17 11 12 6 7"/></svg>
          </button>
        )}
        {rightCollapsed && (
          <button className="prj-expand-btn right" onClick={() => setRightCollapsed(false)} title="Paneli goster">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="11 17 6 12 11 7"/><polyline points="18 17 13 12 18 7"/></svg>
          </button>
        )}
        {!rightCollapsed && (
          <button className="prj-collapse-right-btn" onClick={() => setRightCollapsed(true)} title="Paneli daralt">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="13 17 18 12 13 7"/><polyline points="6 17 11 12 6 7"/></svg>
          </button>
        )}
        {!selected && !isNewMode ? (
          <div className="prj-detail-empty">
            <div className="prj-detail-empty-icon">{'\u{1F4C1}'}</div>
            <h3>Proje Seçin</h3>
            <p>Sol listeden bir proje seçin veya yeni proje oluşturun.</p>
            <button className="prj-detail-new-btn" onClick={startNewProject}>+ Yeni Proje Oluştur</button>
          </div>
        ) : (
          <div className="prj-detail-content">
            {/* Header */}
            <div className="prj-detail-header">
              <div style={{ flex: 1 }}>
                <div className="prj-edit-title-wrap">
                  <input
                    className="prj-edit-title"
                    value={editName}
                    onChange={e => handleNameChange(e.target.value)}
                    placeholder="Proje adi"
                    autoFocus={isNewMode}
                    readOnly={isShared}
                  />
                  <PickBtn field="name" />
                </div>
                <div className="prj-detail-meta">
                  <span className={`prj-detail-status ${isDraft ? 'draft' : 'active'}`}>
                    {isNewMode ? 'Taslak' : selected.currentVersion > 0 ? `v${selected.currentVersion}` : 'Yeni'}
                  </span>
                  {selected && selected.totalSize > 0 && (
                    <>
                      <span className="prj-detail-sep">/</span>
                      <span>{formatSize(selected.totalSize || selected.fileSize)}</span>
                    </>
                  )}
                  {isDirty && <span className="prj-dirty-badge">Kaydedilmemis degisiklikler</span>}
                </div>
              </div>
              <div className="prj-detail-actions">
                {isShared && <span className="prj-shared-badge">Paylasilmis Proje</span>}
                {!isShared && isNewMode && (
                  <button className="prj-btn-outline" onClick={() => { setCreatingNew(false); setPendingFiles({}); localStorage.removeItem(DRAFT_STORAGE_KEY); }}>Vazgec</button>
                )}
                {!isShared && <button
                  className={`prj-btn-save ${isDirty || isNewMode ? '' : 'disabled'}`}
                  onClick={handleSave}
                  disabled={(!isDirty && !isNewMode) || saving}
                >
                  {saving ? 'Kaydediliyor...' : isNewMode ? 'Oluştur' : isDirty ? 'Değişiklikleri Kaydet' : 'Kaydet'}
                </button>}
                {selected && canOpenInEditor && (() => {
                  // Collect all openable files from attachments.
                  // JBZ artık tüm paydaşlara önizleme/not için açık; sadece
                  // indirme butonu disipline göre gizleniyor (canDownloadCat).
                  const openableFiles = [];
                  for (const cat of FILE_CATEGORIES) {
                    const files = attachments[cat.key];
                    if (files && files.length > 0) {
                      for (const f of files) {
                        const ext = f.name.split('.').pop().toLowerCase();
                        if (ext === 'dxf' || ext === 'dwg' || ext === 'jbz') {
                          openableFiles.push({ label: f.name, cat: cat.key, file: f.storedName });
                        }
                      }
                    }
                  }
                  if (openableFiles.length === 0) return null;

                  const openFile = (item) => {
                    if (!item.cat) {
                      window.open(`/app/${selected.id}`, '_blank');
                    } else {
                      window.open(`/app/${selected.id}?attachCat=${item.cat}&attachFile=${encodeURIComponent(item.file)}`, '_blank');
                    }
                  };

                  if (openableFiles.length === 1) {
                    return <button className="prj-btn-primary" onClick={() => openFile(openableFiles[0])}>Editorde Ac</button>;
                  }

                  return (
                    <select
                      className="prj-btn-primary prj-editor-select"
                      value=""
                      onChange={e => {
                        const idx = parseInt(e.target.value, 10);
                        if (!isNaN(idx)) openFile(openableFiles[idx]);
                        e.target.value = '';
                      }}
                    >
                      <option value="" disabled>Editorde Ac...</option>
                      {openableFiles.map((item, i) => (
                        <option key={i} value={i}>{item.label}</option>
                      ))}
                    </select>
                  );
                })()}
                {selected && !isShared && (
                  <button className="prj-btn-ghost danger" onClick={() => handleDelete(selected.id)}>Sil</button>
                )}
              </div>
            </div>

            {/* Proje Dosyalari */}
            <div className={`prj-fm-section ${filesOpen ? 'open' : ''}`}>
              <button className="prj-fm-toggle" onClick={() => setFilesOpen(p => !p)}>
                <h3 className="prj-fm-title">Proje Dosyaları</h3>
                <svg className="prj-fm-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
              </button>
              <div className="prj-fm-body">
              <div className="prj-fm-scroll" ref={fmScrollRef} onMouseDown={handleFmScrollMouseDown}>
                {FILE_CATEGORIES.map(cat => {
                  const apiFiles = attachments[cat.key] || [];
                  const localFiles = (pendingFiles[cat.key] || []).map(f => ({ storedName: f.name, name: f.name, size: f.size, local: true }));
                  const files = isNewMode ? localFiles : apiFiles;
                  const isDragOver = dragOverCat === cat.key;
                  const isUploading = uploadingCat === cat.key;
                  return (
                    <div
                      key={cat.key}
                      className={`prj-fm-card ${isDragOver ? 'dragover' : ''}`}
                      onDragOver={e => { if (canUploadToCategory(cat.key)) { e.preventDefault(); setDragOverCat(cat.key); } }}
                      onDragLeave={() => setDragOverCat(null)}
                      onDrop={e => {
                        e.preventDefault();
                        setDragOverCat(null);
                        if (canUploadToCategory(cat.key)) handleFileDrop(cat.key, Array.from(e.dataTransfer.files));
                      }}
                    >
                      <div className="prj-fm-card-head">
                        <span className={`prj-fm-folder-icon ${cat.key}`}>{cat.icon}</span>
                        <span className="prj-fm-card-label">{cat.label}</span>
                        {files.length > 0 && <span className="prj-fm-folder-count">{files.length}</span>}
                      </div>
                      <div className="prj-fm-card-body">
                        {files.length > 0 ? (
                          <div className="prj-fm-files">
                            {files.map(f => {
                              const isActive = previewFileInfo?.cat === cat.key && previewFileInfo?.storedName === (f.storedName || f.name);
                              const ext = f.name.split('.').pop().toLowerCase();
                              const isPreviewable = ['dxf', 'dwg', 'pdf', 'jbz'].includes(ext);
                              return (
                                <div
                                  key={f.storedName}
                                  className={`prj-fm-file ${isActive ? 'active' : ''}`}
                                  onClick={() => isPreviewable && handleFilePreview(cat.key, f)}
                                  style={isPreviewable ? { cursor: 'pointer' } : undefined}
                                  title={isPreviewable ? `Ön izleme: ${f.name}` : f.name}
                                >
                                  <svg className="prj-fm-file-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                                  <span className="prj-fm-file-name">{f.name}</span>
                                  {selectedId && f.storedName && canDownloadCat(cat) && (
                                    <button
                                      className="prj-fm-file-dl"
                                      title="İndir"
                                      onClick={e => {
                                        e.stopPropagation();
                                        const token = localStorage.getItem('projabze_token');
                                        fetch(`${API_URL}/api/projects/${selectedId}/attachments/${cat.key}/${f.storedName}`, {
                                          headers: { Authorization: `Bearer ${token}` },
                                        })
                                          .then(r => r.blob())
                                          .then(blob => {
                                            const a = document.createElement('a');
                                            a.href = URL.createObjectURL(blob);
                                            a.download = f.name;
                                            a.click();
                                            URL.revokeObjectURL(a.href);
                                          })
                                          .catch(() => {});
                                      }}
                                    >
                                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                                    </button>
                                  )}
                                  <button
                                    className="prj-fm-file-del"
                                    title="Sil"
                                    onClick={e => {
                                      e.stopPropagation();
                                      if (!window.confirm(`"${f.name}" dosyasini silmek istediginize emin misiniz?`)) return;
                                      handleFileDelete(cat.key, f.storedName);
                                    }}
                                  >
                                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="prj-fm-card-empty">Dosya yok</div>
                        )}
                      </div>
                      {canUploadToCategory(cat.key) && (
                        <>
                          <input
                            ref={el => fileInputRefs.current[cat.key] = el}
                            type="file"
                            accept={cat.accept}
                            multiple
                            style={{ position: 'absolute', width: 0, height: 0, opacity: 0, pointerEvents: 'none' }}
                            onChange={e => {
                              if (e.target.files.length) handleFileDrop(cat.key, Array.from(e.target.files));
                              e.target.value = '';
                            }}
                          />
                          <button
                            className="prj-fm-card-add"
                            onClick={() => fileInputRefs.current[cat.key]?.click()}
                            disabled={isUploading}
                          >
                            {isUploading ? 'Yükleniyor...' : '+ Dosya Ekle'}
                          </button>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
              </div>
            </div>

            {/* Proje On Izleme */}
            <div className={`prj-preview-section ${pickMode ? 'pick-active' : ''}`}>
              {previewLoading ? (
                <div className="prj-preview-loading" style={{ height: previewHeight }}>
                  <div className="prj-preview-spinner"></div>
                  <span>Dosya yükleniyor...</span>
                </div>
              ) : previewDxf ? (
                <DxfViewer key={`dxf-${previewKey}`} dxfData={previewDxf} height={previewHeight} className="prj-preview-viewer" pickMode={pickMode} onTextPick={handleTextPick} />
              ) : previewFile ? (
                // Varsayılan görüntüleme → GERÇEK editör (iframe). projectId ile
                // /app/{id}?preview=1 = tam olarak "Editörde Aç" akışı (chrome gizli):
                // loadProjectIntoEditor + tüm hook'lar → duvarlar+tesisat hizalı,
                // editörle BİREBİR. Not-iğneleme (pickMode) aktifken JbzViewer'a
                // düşer — iğneleme/notlar korunur.
                (!pickMode && selected?.id) ? (
                  <EditorPreview
                    key={`ed-${selected.id}`}
                    projectId={selected.id}
                    height={previewHeight}
                    className="prj-preview-viewer"
                  />
                ) : (
                  <JbzViewer key={`jbz-${previewKey}`} file={previewFile} height={previewHeight} className="prj-preview-viewer" pickMode={pickMode} onTextPick={handleTextPick} projectId={selected?.id} zoomTarget={viewerZoomTarget} />
                )
              ) : previewPdf ? (
                <PdfViewer key={`pdf-${previewKey}`} file={previewPdf} height={previewHeight} className="prj-preview-viewer" />
              ) : previewError ? (
                <div className="prj-preview-empty">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                  <span>Ön izleme hatası</span>
                  <small style={{ fontSize: '11px', opacity: 0.7, maxWidth: 260, textAlign: 'center', marginTop: 4 }}>{previewError}</small>
                </div>
              ) : selected && selected.currentVersion > 0 ? (
                <div className="prj-preview-empty">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2" ry="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>
                  <span>Ön izleme yüklenemedi</span>
                </div>
              ) : (
                <div className="prj-preview-empty">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                  <span>{isDraft ? 'Dosya ekleyince ön izleme oluşturulacak' : 'Henüz kaydedilmiş çizim yok'}</span>
                </div>
              )}
              <div className="prj-preview-resize-handle" onMouseDown={handlePreviewResizeStart}>
                <span className="prj-preview-resize-grip"></span>
              </div>
            </div>

            {/* ─── Detail Tabs ─── */}
            <div className="prj-detail-tabs">
              <button className={`prj-detail-tab ${detailTab === 'info' ? 'active' : ''}`} onClick={() => setDetailTab('info')}>
                Proje Bilgileri
              </button>
              <button className={`prj-detail-tab ${detailTab === 'chat' ? 'active' : ''}`} onClick={() => setDetailTab('chat')}>
                Sohbet
                {chatMessages.filter(m => !m.isRead && m.senderId !== user?.id).length > 0 && (
                  <span className="prj-chat-badge" />
                )}
              </button>
              <button className={`prj-detail-tab ${detailTab === 'notes' ? 'active' : ''}`} onClick={() => setDetailTab('notes')}>
                Notlar
                {notesList.length > 0 && <span className="prj-tab-count">{notesList.length}</span>}
              </button>
            </div>

            {detailTab === 'info' && (<div className={isShared ? 'prj-readonly-form' : ''}>
            {/* Bana Özel (sadece proje sahibi) */}
            {!isShared && (
              <div className={`prj-accordion prj-accordion-personal ${openSections.personal ? 'open' : ''}`}>
                <button className="prj-accordion-header" onClick={() => toggleSection('personal')}>
                  <h3>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{verticalAlign:'-2px',marginRight:'6px'}}>
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                      <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                    </svg>
                    Bana Özel
                  </h3>
                  <svg className="prj-accordion-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
                </button>
                <div className="prj-accordion-body">
                  <div className="prj-personal-access-note">
                    Bu alan yalnızca sizin tarafınızdan görülür. Paylaşılan kullanıcılar bu bilgilere erişemez.
                  </div>
                  <div className="prj-info-grid">
                    <div className="prj-info-cell">
                      <label className="prj-info-cell-label">Proje Fiyatı (₺)</label>
                      <div className="prj-info-cell-input">
                        <input className="prj-edit-input" type="text" value={editMeta.price || ''} onChange={e => handleMetaChange('price', e.target.value)} placeholder="orn: 150000" />
                      </div>
                    </div>
                    <div className="prj-info-cell">
                      <label className="prj-info-cell-label">Ödeme Durumu</label>
                      <div className="prj-info-cell-input">
                        <select className="prj-edit-input" value={editMeta.paymentStatus || ''} onChange={e => handleMetaChange('paymentStatus', e.target.value)}>
                          <option value="">Seçin...</option>
                          <option value="unpaid">Ödenmedi</option>
                          <option value="partial">Kısmi Ödendi</option>
                          <option value="paid">Ödendi</option>
                        </select>
                      </div>
                    </div>
                    <div className="prj-info-cell">
                      <label className="prj-info-cell-label">Maliyet (₺)</label>
                      <div className="prj-info-cell-input">
                        <input className="prj-edit-input" type="text" value={editMeta.cost || ''} onChange={e => handleMetaChange('cost', e.target.value)} placeholder="orn: 90000" />
                      </div>
                    </div>
                    <div className="prj-info-cell">
                      <label className="prj-info-cell-label">Teslim Tarihi</label>
                      <div className="prj-info-cell-input">
                        <input className="prj-edit-input" type="date" value={editMeta.deliveryDate || ''} onChange={e => handleMetaChange('deliveryDate', e.target.value)} />
                      </div>
                    </div>
                    <div className="prj-info-cell prj-info-cell-textarea prj-info-cell-full">
                      <label className="prj-info-cell-label">Özel Notlar</label>
                      <div className="prj-info-cell-input">
                        <textarea className="prj-edit-input prj-edit-textarea" value={editMeta.personalNotes || ''} onChange={e => handleMetaChange('personalNotes', e.target.value)} placeholder="Sadece sizin göreceğiniz notlar..." rows={2} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Genel Bilgiler */}
            <div className={`prj-accordion ${openSections.genel ? 'open' : ''}`}>
              <button className="prj-accordion-header" onClick={() => toggleSection('genel')}>
                <h3>Genel Bilgiler<span className="prj-access-chip" title="Projeyi paylaştığın kişiler görebilir"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>Paylaşılanlar</span></h3>
                <svg className="prj-accordion-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
              </button>
              <div className="prj-accordion-body">
                <div className="prj-info-grid">
                  <div className="prj-info-cell">
                    <label className="prj-info-cell-label">Proje Tipi</label>
                    <div className="prj-info-cell-input">
                      <select className="prj-edit-input" value={editMeta.projectType || ''} onChange={e => handleMetaChange('projectType', e.target.value)}>
                        <option value="">Seçin...</option>
                        {PROJECT_TYPES.map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="prj-info-cell">
                    <label className="prj-info-cell-label">Telefon</label>
                    <div className="prj-info-cell-input">
                      <input className="prj-edit-input" type="text" value={editMeta.phone || ''} onChange={e => handleMetaChange('phone', e.target.value)} placeholder="Iletisim numarasi" />
                      <PickBtn field="phone" />
                    </div>
                  </div>
                  <div className="prj-info-cell">
                    <label className="prj-info-cell-label">Arsa Alanı (m²)</label>
                    <div className="prj-info-cell-input">
                      <input className="prj-edit-input" type="text" value={editMeta.landArea || ''} onChange={e => handleMetaChange('landArea', e.target.value)} placeholder="orn: 1200" />
                      <PickBtn field="landArea" />
                    </div>
                  </div>
                  <div className="prj-info-cell">
                    <label className="prj-info-cell-label">İnşaat Alanı (m²)</label>
                    <div className="prj-info-cell-input">
                      <input className="prj-edit-input" type="text" value={editMeta.buildingArea || ''} onChange={e => handleMetaChange('buildingArea', e.target.value)} placeholder="orn: 3500" />
                      <PickBtn field="buildingArea" />
                    </div>
                  </div>
                  <div className="prj-info-cell">
                    <label className="prj-info-cell-label">Kat Adedi</label>
                    <div className="prj-info-cell-input">
                      <input className="prj-edit-input" type="text" value={editMeta.floorCount || ''} onChange={e => handleMetaChange('floorCount', e.target.value)} placeholder="orn: 5" />
                      <PickBtn field="floorCount" />
                    </div>
                  </div>
                  <div className="prj-info-cell">
                    <label className="prj-info-cell-label">Yapı Sınıfı</label>
                    <div className="prj-info-cell-input">
                      <input className="prj-edit-input" type="text" value={editMeta.buildingClass || ''} onChange={e => handleMetaChange('buildingClass', e.target.value)} placeholder="orn: 3A" />
                      <PickBtn field="buildingClass" />
                    </div>
                  </div>
                  <div className="prj-info-cell">
                    <label className="prj-info-cell-label">Bağımsız Bölüm</label>
                    <div className="prj-info-cell-input">
                      <input
                        className="prj-edit-input"
                        type="number"
                        min="0"
                        value={editMeta.independentUnits || ''}
                        onChange={e => {
                          const v = e.target.value;
                          handleMetaChange('independentUnits', v);
                          const prevUnits = editMeta.independentUnits;
                          const currentMeter = editMeta.meterCount;
                          if (!currentMeter || String(currentMeter) === String(prevUnits || '')) {
                            handleMetaChange('meterCount', v);
                          }
                        }}
                        placeholder="orn: 24"
                      />
                    </div>
                  </div>
                  <div className="prj-info-cell">
                    <label className="prj-info-cell-label">Sayaç Sayısı</label>
                    <div className="prj-info-cell-input">
                      <input
                        className="prj-edit-input"
                        type="number"
                        min="0"
                        value={editMeta.meterCount || ''}
                        onChange={e => handleMetaChange('meterCount', e.target.value)}
                        placeholder="bağımsız bölüm"
                      />
                    </div>
                  </div>
                  <div className="prj-info-cell">
                    <label className="prj-info-cell-label">Asansör Sayısı</label>
                    <div className="prj-info-cell-input">
                      <input className="prj-edit-input" type="number" min="0" value={editMeta.elevatorCount || ''} onChange={e => handleMetaChange('elevatorCount', e.target.value)} placeholder="orn: 2" />
                    </div>
                  </div>
                  <div className="prj-info-cell">
                    <label className="prj-info-cell-label">Enerji Odası</label>
                    <div className="prj-info-cell-input">
                      <input className="prj-edit-input" type="number" min="0" value={editMeta.energyRoomCount || ''} onChange={e => handleMetaChange('energyRoomCount', e.target.value)} placeholder="orn: 1" />
                    </div>
                  </div>
                  <div className="prj-info-cell">
                    <label className="prj-info-cell-label">Kablo Bacası</label>
                    <div className="prj-info-cell-input">
                      <input className="prj-edit-input" type="number" min="0" value={editMeta.cableShaftCount || ''} onChange={e => handleMetaChange('cableShaftCount', e.target.value)} placeholder="orn: 2" />
                    </div>
                  </div>
                  <div className="prj-info-cell">
                    <label className="prj-info-cell-label">Sistem Odası</label>
                    <div className="prj-info-cell-input">
                      <input className="prj-edit-input" type="number" min="0" value={editMeta.systemRoomCount || ''} onChange={e => handleMetaChange('systemRoomCount', e.target.value)} placeholder="orn: 1" />
                    </div>
                  </div>
                  <div className="prj-info-cell">
                    <label className="prj-info-cell-label">Zayıf Akım Bacası</label>
                    <div className="prj-info-cell-input">
                      <input className="prj-edit-input" type="number" min="0" value={editMeta.weakCurrentShaftCount || ''} onChange={e => handleMetaChange('weakCurrentShaftCount', e.target.value)} placeholder="orn: 1" />
                    </div>
                  </div>
                  <div className="prj-info-cell prj-info-cell-textarea prj-info-cell-full">
                    <label className="prj-info-cell-label">Notlar</label>
                    <div className="prj-info-cell-input">
                      <textarea className="prj-edit-input prj-edit-textarea" value={editMeta.notes || ''} onChange={e => handleMetaChange('notes', e.target.value)} placeholder="Ek notlar..." rows={2} />
                    </div>
                  </div>
                  {/* Editör Şablonu — otomatik öneri + manuel seçim */}
                  {(() => {
                    const autoDetected = detectProjectType({
                      floorCount: Number(editMeta.floorCount) || 1,
                      meterCount: Number(editMeta.meterCount) || Number(editMeta.independentUnits) || 1,
                      panelCount: Number(editMeta.independentUnits) || 1,
                      hasEnergyRoom: Number(editMeta.energyRoomCount) > 0 || Number(editMeta.floorCount) >= 10,
                    });
                    const current = editMeta.editorTemplate || autoDetected;
                    return (
                      <div className="prj-info-cell prj-info-cell-full">
                        <label className="prj-info-cell-label">Editör Şablonu</label>
                        <div className="prj-info-cell-input">
                          <select
                            className="prj-edit-input"
                            value={current}
                            onChange={e => handleMetaChange('editorTemplate', e.target.value)}
                          >
                            <option value={EDITOR_TEMPLATE_TYPES.SINGLE_UNIT}>{EDITOR_TEMPLATE_LABELS[EDITOR_TEMPLATE_TYPES.SINGLE_UNIT]}</option>
                            <option value={EDITOR_TEMPLATE_TYPES.MULTI_FLOOR_SINGLE_METER}>{EDITOR_TEMPLATE_LABELS[EDITOR_TEMPLATE_TYPES.MULTI_FLOOR_SINGLE_METER]}</option>
                            <option value={EDITOR_TEMPLATE_TYPES.MULTI_METER}>{EDITOR_TEMPLATE_LABELS[EDITOR_TEMPLATE_TYPES.MULTI_METER]}</option>
                            <option value={EDITOR_TEMPLATE_TYPES.HIGH_RISE} disabled>{EDITOR_TEMPLATE_LABELS[EDITOR_TEMPLATE_TYPES.HIGH_RISE]} (yakında)</option>
                          </select>
                          {editMeta.editorTemplate && editMeta.editorTemplate !== autoDetected && (
                            <button
                              type="button"
                              className="prj-btn-secondary"
                              onClick={() => handleMetaChange('editorTemplate', '')}
                              title="Otomatik seçime dön"
                              style={{ padding: '0 .55rem', fontSize: '.7rem' }}
                            >
                              Otomatik
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>

            {/* Adres Bilgileri */}
            <div className={`prj-accordion ${openSections.adres ? 'open' : ''}`}>
              <button className="prj-accordion-header" onClick={() => toggleSection('adres')}>
                <h3>Adres Bilgileri<span className="prj-access-chip" title="Projeyi paylaştığın kişiler görebilir"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>Paylaşılanlar</span></h3>
                <svg className="prj-accordion-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
              </button>
              <div className="prj-accordion-body">
                <div className="prj-info-grid">
                  <div className="prj-info-cell">
                    <label className="prj-info-cell-label">İl</label>
                    <div className="prj-info-cell-input">
                      <AutocompleteInput
                        className="prj-edit-input"
                        value={editMeta.city || ''}
                        onChange={val => {
                          handleMetaChange('city', val);
                          if (val !== editMeta.city) {
                            handleMetaChange('district', '');
                            // Seçilen ile yetkili EPDK dağıtım şirketini otomatik doldur
                            if (val && CITY_LIST.includes(val)) {
                              const distFirms = (firmsMap['elektrik_dagitim'] || []).filter(f => f.id?.startsWith('dist:'));
                              const match = distFirms.find(f => Array.isArray(f.iller) && f.iller.includes(val));
                              if (match) {
                                handleMetaChange('elektrikDagitimId', match.id);
                                handleMetaChange('elektrikDagitimName', match.name);
                              }
                            }
                          }
                        }}
                        options={CITY_LIST}
                        placeholder="İl seçin veya yazın..."
                      />
                      <PickBtn field="city" />
                    </div>
                  </div>
                  <div className="prj-info-cell">
                    <label className="prj-info-cell-label">İlçe</label>
                    <div className="prj-info-cell-input">
                      <AutocompleteInput
                        className="prj-edit-input"
                        value={editMeta.district || ''}
                        onChange={val => handleMetaChange('district', val)}
                        options={getDistricts(editMeta.city)}
                        placeholder={editMeta.city ? 'İlçe seçin...' : 'Önce il seçin'}
                      />
                      <PickBtn field="district" />
                    </div>
                  </div>
                  <div className="prj-info-cell">
                    <label className="prj-info-cell-label">Mahalle</label>
                    <div className="prj-info-cell-input">
                      <input className="prj-edit-input" type="text" value={editMeta.neighborhood || ''} onChange={e => handleMetaChange('neighborhood', e.target.value)} placeholder="Mahalle" />
                      <PickBtn field="neighborhood" />
                    </div>
                  </div>
                  <div className="prj-info-cell prj-info-cell-span2">
                    <label className="prj-info-cell-label">Cadde / Sokak</label>
                    <div className="prj-info-cell-input">
                      <input className="prj-edit-input" type="text" value={editMeta.street || ''} onChange={e => handleMetaChange('street', e.target.value)} placeholder="Cadde / Sokak / No" />
                      <PickBtn field="street" />
                    </div>
                  </div>
                  <div className="prj-info-cell">
                    <label className="prj-info-cell-label">Ada</label>
                    <div className="prj-info-cell-input">
                      <input className="prj-edit-input" type="text" value={editMeta.ada || ''} onChange={e => handleMetaChange('ada', e.target.value)} placeholder="Ada" />
                      <PickBtn field="ada" />
                    </div>
                  </div>
                  <div className="prj-info-cell">
                    <label className="prj-info-cell-label">Pafta</label>
                    <div className="prj-info-cell-input">
                      <input className="prj-edit-input" type="text" value={editMeta.pafta || ''} onChange={e => handleMetaChange('pafta', e.target.value)} placeholder="Pafta" />
                      <PickBtn field="pafta" />
                    </div>
                  </div>
                  <div className="prj-info-cell">
                    <label className="prj-info-cell-label">Parsel</label>
                    <div className="prj-info-cell-input">
                      <input className="prj-edit-input" type="text" value={editMeta.parsel || ''} onChange={e => handleMetaChange('parsel', e.target.value)} placeholder="Parsel" />
                      <PickBtn field="parsel" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Künye / Başlık Bilgileri — Başlık dokümanına aktarılacak ek alanlar */}
            <div className={`prj-accordion ${openSections.kunye ? 'open' : ''}`}>
              <button className="prj-accordion-header" onClick={() => toggleSection('kunye')}>
                <h3>Künye / Başlık Bilgileri<span className="prj-access-chip" title="Projeyi paylaştığın kişiler görebilir"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>Paylaşılanlar</span></h3>
                <svg className="prj-accordion-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
              </button>
              <div className="prj-accordion-body">
                <div className="prj-info-grid">
                  <div className="prj-info-cell">
                    <label className="prj-info-cell-label">Proje No</label>
                    <div className="prj-info-cell-input">
                      <input className="prj-edit-input" type="text" value={editMeta.projectNo || ''} onChange={e => handleMetaChange('projectNo', e.target.value)} placeholder="Örn: 2026/01" />
                    </div>
                  </div>
                  <div className="prj-info-cell">
                    <label className="prj-info-cell-label">Kullanım Amacı</label>
                    <div className="prj-info-cell-input">
                      <select className="prj-edit-input" value={editMeta.purposeOfUse || ''} onChange={e => handleMetaChange('purposeOfUse', e.target.value)}>
                        <option value="">Seçin...</option>
                        <option value="MESKEN">MESKEN</option>
                        <option value="TİCARİ">TİCARİ</option>
                        <option value="KARMA">KARMA</option>
                        <option value="RESMİ">RESMİ</option>
                        <option value="EĞİTİM">EĞİTİM</option>
                        <option value="SAĞLIK">SAĞLIK</option>
                        <option value="ENDÜSTRİYEL">ENDÜSTRİYEL</option>
                      </select>
                    </div>
                  </div>
                  <div className="prj-info-cell">
                    <label className="prj-info-cell-label">Yapım Süresi</label>
                    <div className="prj-info-cell-input">
                      <input className="prj-edit-input" type="text" value={editMeta.constructionDuration || ''} onChange={e => handleMetaChange('constructionDuration', e.target.value)} placeholder="Örn: 18 ay" />
                    </div>
                  </div>
                  <div className="prj-info-cell">
                    <label className="prj-info-cell-label">Ölçek</label>
                    <div className="prj-info-cell-input">
                      <input className="prj-edit-input" type="text" value={editMeta.scale || ''} onChange={e => handleMetaChange('scale', e.target.value)} placeholder="1/50" />
                    </div>
                  </div>
                  <div className="prj-info-cell">
                    <label className="prj-info-cell-label">Çizen</label>
                    <div className="prj-info-cell-input">
                      <input className="prj-edit-input" type="text" value={editMeta.drawnBy || ''} onChange={e => handleMetaChange('drawnBy', e.target.value)} placeholder="Çizimi yapan kişi" />
                    </div>
                  </div>
                  <div className="prj-info-cell">
                    <label className="prj-info-cell-label">Belediye</label>
                    <div className="prj-info-cell-input">
                      <input className="prj-edit-input" type="text" value={editMeta.municipality || ''} onChange={e => handleMetaChange('municipality', e.target.value)} placeholder={editMeta.district ? `Örn: ${editMeta.district} Belediyesi` : 'Belediye adı'} />
                    </div>
                  </div>
                  <div className="prj-info-cell prj-info-cell-span2">
                    <label className="prj-info-cell-label">Onay Yapacak Kurum</label>
                    <div className="prj-info-cell-input">
                      <input className="prj-edit-input" type="text" value={editMeta.approvalAuthority || ''} onChange={e => handleMetaChange('approvalAuthority', e.target.value)} placeholder="Onay yapacak kurum / şirket" />
                    </div>
                  </div>
                  <div className="prj-info-cell prj-info-cell-span2">
                    <label className="prj-info-cell-label">Yapı Denetim Şirketi</label>
                    <div className="prj-info-cell-input">
                      <input className="prj-edit-input" type="text" value={editMeta.buildingInspectionCo || ''} onChange={e => handleMetaChange('buildingInspectionCo', e.target.value)} placeholder="Yapı denetim firma adı" />
                    </div>
                  </div>
                </div>

                {/* Yapı Sahibi / Mal Sahibi Vergi Bilgileri */}
                <div className="prj-info-grid-sub" style={{ marginTop: 8 }}>
                  <h4 style={{ margin: '8px 0 4px', fontSize: '.78rem', color: '#888', textTransform: 'uppercase', letterSpacing: '.04em' }}>Yapı Sahibi Vergi Bilgileri</h4>
                </div>
                <div className="prj-info-grid">
                  <div className="prj-info-cell">
                    <label className="prj-info-cell-label">Vergi Dairesi</label>
                    <div className="prj-info-cell-input">
                      <input className="prj-edit-input" type="text" value={editMeta.ownerTaxOffice || ''} onChange={e => handleMetaChange('ownerTaxOffice', e.target.value)} placeholder="Vergi dairesi adı" />
                    </div>
                  </div>
                  <div className="prj-info-cell">
                    <label className="prj-info-cell-label">Vergi No</label>
                    <div className="prj-info-cell-input">
                      <input className="prj-edit-input" type="text" value={editMeta.ownerTaxNo || ''} onChange={e => handleMetaChange('ownerTaxNo', e.target.value)} placeholder="TC Kimlik veya Vergi No" />
                    </div>
                  </div>
                </div>

                {/* Elektrik Müellifi / Proje Sorumlusu Mesleki Bilgileri */}
                <div className="prj-info-grid-sub" style={{ marginTop: 8 }}>
                  <h4 style={{ margin: '8px 0 4px', fontSize: '.78rem', color: '#888', textTransform: 'uppercase', letterSpacing: '.04em' }}>Proje Sorumlusu (Elektrik Müellifi) Mesleki Bilgileri</h4>
                </div>
                <div className="prj-info-grid">
                  <div className="prj-info-cell">
                    <label className="prj-info-cell-label">Oda No</label>
                    <div className="prj-info-cell-input">
                      <input className="prj-edit-input" type="text" value={editMeta.authorChamberNo || ''} onChange={e => handleMetaChange('authorChamberNo', e.target.value)} placeholder="EMO sicil no" />
                    </div>
                  </div>
                  <div className="prj-info-cell">
                    <label className="prj-info-cell-label">Diploma No</label>
                    <div className="prj-info-cell-input">
                      <input className="prj-edit-input" type="text" value={editMeta.authorDiplomaNo || ''} onChange={e => handleMetaChange('authorDiplomaNo', e.target.value)} placeholder="Diploma no" />
                    </div>
                  </div>
                  <div className="prj-info-cell">
                    <label className="prj-info-cell-label">Vergi Sicil No</label>
                    <div className="prj-info-cell-input">
                      <input className="prj-edit-input" type="text" value={editMeta.authorTaxRegNo || ''} onChange={e => handleMetaChange('authorTaxRegNo', e.target.value)} placeholder="Vergi sicil no" />
                    </div>
                  </div>
                  <div className="prj-info-cell">
                    <label className="prj-info-cell-label">Vergi Dairesi</label>
                    <div className="prj-info-cell-input">
                      <input className="prj-edit-input" type="text" value={editMeta.authorTaxOffice || ''} onChange={e => handleMetaChange('authorTaxOffice', e.target.value)} placeholder="Vergi dairesi adı" />
                    </div>
                  </div>
                </div>

                {/* Proje Uygulama Sorumlusu (Müellif'ten farklıysa) */}
                <div className="prj-info-grid-sub" style={{ marginTop: 8 }}>
                  <h4 style={{ margin: '8px 0 4px', fontSize: '.78rem', color: '#888', textTransform: 'uppercase', letterSpacing: '.04em' }}>Proje Uygulama Sorumlusu (boş bırakılırsa Müellif kullanılır)</h4>
                </div>
                <div className="prj-info-grid">
                  <div className="prj-info-cell prj-info-cell-span2">
                    <label className="prj-info-cell-label">Adı Soyadı</label>
                    <div className="prj-info-cell-input">
                      <input className="prj-edit-input" type="text" value={editMeta.implManagerName || ''} onChange={e => handleMetaChange('implManagerName', e.target.value)} placeholder="Adı Soyadı" />
                    </div>
                  </div>
                  <div className="prj-info-cell">
                    <label className="prj-info-cell-label">Oda No</label>
                    <div className="prj-info-cell-input">
                      <input className="prj-edit-input" type="text" value={editMeta.implManagerChamberNo || ''} onChange={e => handleMetaChange('implManagerChamberNo', e.target.value)} placeholder="EMO sicil no" />
                    </div>
                  </div>
                  <div className="prj-info-cell">
                    <label className="prj-info-cell-label">Diploma No</label>
                    <div className="prj-info-cell-input">
                      <input className="prj-edit-input" type="text" value={editMeta.implManagerDiplomaNo || ''} onChange={e => handleMetaChange('implManagerDiplomaNo', e.target.value)} placeholder="Diploma no" />
                    </div>
                  </div>
                  <div className="prj-info-cell">
                    <label className="prj-info-cell-label">Vergi Sicil No</label>
                    <div className="prj-info-cell-input">
                      <input className="prj-edit-input" type="text" value={editMeta.implManagerTaxRegNo || ''} onChange={e => handleMetaChange('implManagerTaxRegNo', e.target.value)} placeholder="Vergi sicil no" />
                    </div>
                  </div>
                  <div className="prj-info-cell">
                    <label className="prj-info-cell-label">Vergi Dairesi</label>
                    <div className="prj-info-cell-input">
                      <input className="prj-edit-input" type="text" value={editMeta.implManagerTaxOffice || ''} onChange={e => handleMetaChange('implManagerTaxOffice', e.target.value)} placeholder="Vergi dairesi adı" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Müellif Bilgileri — Atanan elektrik müellifinin profilinden otomatik gelen veriler */}
            <div className={`prj-accordion ${openSections.muellifBilgi ? 'open' : ''}`}>
              <button className="prj-accordion-header" onClick={() => toggleSection('muellifBilgi')}>
                <h3>Müellif Bilgileri<span className="prj-access-chip" title="Atanan müellifin profilinden otomatik gelir"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>Otomatik</span></h3>
                <svg className="prj-accordion-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
              </button>
              <div className="prj-accordion-body">
                {!editMeta.elektrikMuellifMemberId && (
                  <div style={{ padding: '8px 4px', color: '#888', fontSize: '.82rem', fontStyle: 'italic' }}>
                    Paydaşlar bölümünden bir <strong>Elektrik Müellifi</strong> atayın — mesleki bilgileri burada otomatik görünür ve başlığa aktarılır.
                  </div>
                )}
                {editMeta.elektrikMuellifMemberId && muellifProfileLoading && (
                  <div style={{ padding: '8px 4px', color: '#888', fontSize: '.82rem' }}>Müellif profili yükleniyor…</div>
                )}
                {editMeta.elektrikMuellifMemberId && !muellifProfileLoading && !muellifProfile && (
                  <div style={{ padding: '8px 4px', color: '#c87', fontSize: '.82rem' }}>Müellif profili alınamadı.</div>
                )}
                {muellifProfile && (
                  <>
                    {/* Kişi Özeti */}
                    <div className="prj-info-grid-sub">
                      <h4 style={{ margin: '0 0 6px', fontSize: '.78rem', color: '#888', textTransform: 'uppercase', letterSpacing: '.04em' }}>Kişi</h4>
                    </div>
                    <div className="prj-info-grid">
                      <div className="prj-info-cell">
                        <label className="prj-info-cell-label">Adı Soyadı</label>
                        <div className="prj-info-cell-input">
                          <input className="prj-edit-input" type="text" value={`${muellifProfile.user?.firstName || ''} ${muellifProfile.user?.lastName || ''}`.trim()} readOnly />
                        </div>
                      </div>
                      <div className="prj-info-cell">
                        <label className="prj-info-cell-label">E-posta</label>
                        <div className="prj-info-cell-input">
                          <input className="prj-edit-input" type="text" value={muellifProfile.user?.email || ''} readOnly />
                        </div>
                      </div>
                      <div className="prj-info-cell">
                        <label className="prj-info-cell-label">Telefon</label>
                        <div className="prj-info-cell-input">
                          <input className="prj-edit-input" type="text" value={muellifProfile.profile?.phone || muellifProfile.user?.phone || ''} readOnly />
                        </div>
                      </div>
                      <div className="prj-info-cell">
                        <label className="prj-info-cell-label">Uzmanlık</label>
                        <div className="prj-info-cell-input">
                          <input className="prj-edit-input" type="text" value={muellifProfile.profile?.specialty || ''} readOnly />
                        </div>
                      </div>
                    </div>

                    {/* Mesleki Bilgiler */}
                    <div className="prj-info-grid-sub" style={{ marginTop: 8 }}>
                      <h4 style={{ margin: '8px 0 4px', fontSize: '.78rem', color: '#888', textTransform: 'uppercase', letterSpacing: '.04em' }}>Mesleki Bilgiler</h4>
                    </div>
                    <div className="prj-info-grid">
                      <div className="prj-info-cell">
                        <label className="prj-info-cell-label">Oda Sicil No</label>
                        <div className="prj-info-cell-input">
                          <input className="prj-edit-input" type="text" value={muellifProfile.profile?.chamberNo || ''} readOnly />
                        </div>
                      </div>
                      <div className="prj-info-cell">
                        <label className="prj-info-cell-label">Diploma No</label>
                        <div className="prj-info-cell-input">
                          <input className="prj-edit-input" type="text" value={muellifProfile.profile?.diplomaNo || ''} readOnly />
                        </div>
                      </div>
                      <div className="prj-info-cell">
                        <label className="prj-info-cell-label">Büro Tescil No</label>
                        <div className="prj-info-cell-input">
                          <input className="prj-edit-input" type="text" value={muellifProfile.profile?.buroTescilNo || ''} readOnly />
                        </div>
                      </div>
                      <div className="prj-info-cell">
                        <label className="prj-info-cell-label">Vergi No</label>
                        <div className="prj-info-cell-input">
                          <input className="prj-edit-input" type="text" value={muellifProfile.profile?.vergiNo || ''} readOnly />
                        </div>
                      </div>
                      <div className="prj-info-cell prj-info-cell-span2">
                        <label className="prj-info-cell-label">Vergi Dairesi</label>
                        <div className="prj-info-cell-input">
                          <input className="prj-edit-input" type="text" value={muellifProfile.profile?.vergiDairesi || ''} readOnly />
                        </div>
                      </div>
                    </div>

                    {/* Firma / Şirket */}
                    {muellifProfile.company && (muellifProfile.company.name || muellifProfile.company.taxNo) && (
                      <>
                        <div className="prj-info-grid-sub" style={{ marginTop: 8 }}>
                          <h4 style={{ margin: '8px 0 4px', fontSize: '.78rem', color: '#888', textTransform: 'uppercase', letterSpacing: '.04em' }}>Şirket / Büro</h4>
                        </div>
                        <div className="prj-info-grid">
                          <div className="prj-info-cell prj-info-cell-span2">
                            <label className="prj-info-cell-label">Firma Adı</label>
                            <div className="prj-info-cell-input">
                              <input className="prj-edit-input" type="text" value={muellifProfile.company.name || ''} readOnly />
                            </div>
                          </div>
                          <div className="prj-info-cell">
                            <label className="prj-info-cell-label">Firma Vergi No</label>
                            <div className="prj-info-cell-input">
                              <input className="prj-edit-input" type="text" value={muellifProfile.company.taxNo || ''} readOnly />
                            </div>
                          </div>
                          <div className="prj-info-cell">
                            <label className="prj-info-cell-label">Firma Vergi Dairesi</label>
                            <div className="prj-info-cell-input">
                              <input className="prj-edit-input" type="text" value={muellifProfile.company.taxOffice || ''} readOnly />
                            </div>
                          </div>
                          <div className="prj-info-cell prj-info-cell-span2">
                            <label className="prj-info-cell-label">Adres</label>
                            <div className="prj-info-cell-input">
                              <input className="prj-edit-input" type="text" value={[muellifProfile.company.address, muellifProfile.company.district, muellifProfile.company.city].filter(Boolean).join(', ')} readOnly />
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                    <div style={{ marginTop: 8, padding: '6px 8px', background: 'rgba(26,188,156,0.08)', borderLeft: '2px solid #1abc9c', borderRadius: '0 4px 4px 0', fontSize: '.78rem', color: '#aaa' }}>
                      Bu alanlar müellifin <a href={muellifProfile.user?.id ? `/profile/${muellifProfile.user.id}` : '#'} target="_blank" rel="noreferrer" style={{ color: '#1abc9c' }}>kendi profilinden</a> gelir. Künye → "Proje Sorumlusu Mesleki Bilgileri" alanları otomatik dolar; orada değer var ise öncelik kullanıcının yazdığına olur.
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Paydaslar */}
            <div className={`prj-accordion ${openSections.paydaslar ? 'open' : ''}`}>
              <button className="prj-accordion-header" onClick={() => toggleSection('paydaslar')}>
                <h3>Paydaşlar<span className="prj-access-chip" title="Projeyi paylaştığın kişiler görebilir"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>Paylaşılanlar</span></h3>
                <svg className="prj-accordion-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
              </button>
              <div className="prj-accordion-body">
                <div className="prj-edit-form">
                  <div className="prj-edit-row" style={{ marginBottom: 8 }}>
                    <label className="prj-edit-label">Ekipten Ata</label>
                    <div className="prj-edit-input-wrap" style={{ display: 'flex', gap: 6 }}>
                      <select
                        className="prj-team-select"
                        defaultValue=""
                        onFocus={() => { if (teamsList.length === 0 && !teamsLoading) fetchTeamsList(); }}
                        onChange={e => { if (e.target.value) { applyTeamToStakeholders(e.target.value); e.target.value = ''; } }}
                      >
                        <option value="">{teamsLoading ? 'Yükleniyor...' : 'Ekip Seçin...'}</option>
                        {teamsList.map(t => (
                          <option key={t.id} value={t.id}>{t.name} ({t.memberCount} uye)</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className={`prj-stakeholders-3col-head ${isShared ? 'prj-stakeholders-1col-head' : ''}`}>
                    <div className="prj-stakeholders-3col-head-section">KİŞİLER</div>
                    <div className="prj-stakeholders-3col-head-cell">PAYDAŞ</div>
                    {!isShared && (
                      <>
                        <div className="prj-stakeholders-3col-head-cell">Link</div>
                        <div className="prj-stakeholders-3col-head-cell">ERİŞİM</div>
                      </>
                    )}
                  </div>
                  {PERSON_FIELDS.map(pf => {
                    const sa = (editMeta.stakeholderAccess || {})[pf.key] || {};
                    const dk = personFieldDataKeys(pf.key);
                    return (
                      <div className="prj-stakeholders-row" key={pf.key}>
                        <label className="prj-stakeholders-label">{pf.label}</label>
                        <div className="prj-stakeholders-cells">
                          <div className="prj-stakeholders-col">
                            <PersonCombobox
                              fieldKey={pf.key}
                              roleFilter={{ sectorRole: pf.matchSectorRole, discipline: pf.matchDiscipline }}
                              value={{
                                name: editMeta[dk.name] || '',
                                type: editMeta[dk.type] || '',
                                memberId: editMeta[dk.memberId] || '',
                                externalId: editMeta[dk.externalId] || '',
                              }}
                              onChange={({ name, type, memberId, externalId }) => {
                                setEditMeta(prev => ({
                                  ...prev,
                                  [dk.name]: name,
                                  [dk.type]: type,
                                  [dk.memberId]: memberId,
                                  [dk.externalId]: externalId,
                                }));
                                setIsDirty(true);
                              }}
                            />
                          </div>
                          {!isShared && (
                            <>
                              <div className="prj-stakeholders-col">
                                <StakeholderShareCell
                                  projectId={selected?.id}
                                  disabled={isShared}
                                  stakeholderKey={pf.key}
                                  accessConfig={resolveStakeholderAccessConfig(pf.key, sa.override, null)}
                                  currentToken={sa.shareToken || ''}
                                  onTokenChange={token => updateStakeholderAccess(pf.key, { shareToken: token })}
                                />
                              </div>
                              <div className="prj-stakeholders-col">
                                <StakeholderAccessCell
                                  stakeholderKey={pf.key}
                                  value={sa}
                                  onChange={({ templateId, override }) => updateStakeholderAccess(pf.key, { templateId, override })}
                                />
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  {(editMeta.customStakeholders || []).map(cs => {
                    const sa = (editMeta.stakeholderAccess || {})[cs.id] || {};
                    return (
                      <div className="prj-stakeholders-row prj-stakeholders-row-custom" key={cs.id}>
                        <input
                          className="prj-stakeholders-label-input"
                          value={cs.label || ''}
                          onChange={e => updateCustomStakeholder(cs.id, { label: e.target.value })}
                          placeholder="Paydas..."
                        />
                        <div className="prj-stakeholders-cells">
                          <div className="prj-stakeholders-col">
                            <PersonCombobox
                              fieldKey={cs.id}
                              value={{
                                name: cs.name || '',
                                type: cs.type || '',
                                memberId: cs.memberId || '',
                                externalId: cs.externalId || '',
                              }}
                              onChange={({ name, type, memberId, externalId }) =>
                                updateCustomStakeholder(cs.id, { name, type, memberId, externalId })
                              }
                            />
                          </div>
                          {!isShared && (
                            <>
                              <div className="prj-stakeholders-col">
                                <StakeholderShareCell
                                  projectId={selected?.id}
                                  disabled={isShared}
                                  stakeholderKey={cs.id}
                                  accessConfig={resolveStakeholderAccessConfig(cs.id, sa.override, null)}
                                  currentToken={sa.shareToken || ''}
                                  onTokenChange={token => updateStakeholderAccess(cs.id, { shareToken: token })}
                                />
                              </div>
                              <div className="prj-stakeholders-col">
                                <StakeholderAccessCell
                                  stakeholderKey={cs.id}
                                  value={sa}
                                  onChange={({ templateId, override }) => updateStakeholderAccess(cs.id, { templateId, override })}
                                />
                              </div>
                            </>
                          )}
                        </div>
                        {!isShared && (
                          <button
                            type="button"
                            className="prj-stakeholders-remove-btn"
                            onClick={() => removeCustomStakeholder(cs.id)}
                            title="Paydasi kaldir"
                          >×</button>
                        )}
                      </div>
                    );
                  })}
                  {!isShared && (
                    <button
                      type="button"
                      className="prj-stakeholders-add-btn"
                      onClick={addCustomStakeholder}
                    >
                      + Yeni paydas ekle
                    </button>
                  )}
                  <div className="prj-stakeholders-section-row">Kurumlar</div>
                  {FIRM_FIELDS.map(ff => {
                    const sa = (editMeta.stakeholderAccess || {})[ff.key] || {};
                    return (
                      <div className="prj-stakeholders-row" key={ff.key}>
                        <label className="prj-stakeholders-label">{ff.label}</label>
                        <div className="prj-stakeholders-cells">
                          <div className="prj-stakeholders-col">
                            <FirmCombobox
                              fieldKey={ff.key}
                              firmType={ff.type}
                              emptyLabel={ff.emptyLabel}
                              unknownLabel={ff.unknownLabel}
                              value={editMeta[ff.key] || ''}
                              firms={firmsMap[ff.type] || []}
                              onChange={(val, name) => {
                                handleMetaChange(ff.key, val);
                                // Firma ADINI da sakla (ör. elektrikDagitimName) — editörde
                                // supply box etiketi bunu okur (ID'den async çözmeye gerek kalmaz).
                                handleMetaChange(ff.key.replace(/Id$/, 'Name'), name || '');
                              }}
                              onCreateFirm={handleCreateFirm}
                            />
                          </div>
                          {!isShared && (
                            <>
                              <div className="prj-stakeholders-col">
                                <StakeholderShareCell
                                  projectId={selected?.id}
                                  disabled={isShared}
                                  stakeholderKey={ff.key}
                                  accessConfig={resolveStakeholderAccessConfig(ff.key, sa.override, null)}
                                  currentToken={sa.shareToken || ''}
                                  onTokenChange={token => updateStakeholderAccess(ff.key, { shareToken: token })}
                                />
                              </div>
                              <div className="prj-stakeholders-col">
                                <StakeholderAccessCell
                                  stakeholderKey={ff.key}
                                  value={sa}
                                  onChange={({ templateId, override }) => updateStakeholderAccess(ff.key, { templateId, override })}
                                />
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Sistem Bilgileri - only for saved projects */}
            {selected && (
              <div className={`prj-accordion ${openSections.sistem ? 'open' : ''}`}>
                <button className="prj-accordion-header" onClick={() => toggleSection('sistem')}>
                  <h3>Sistem Bilgileri<span className="prj-access-chip" title="Projeyi paylaştığın kişiler görebilir"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>Paylaşılanlar</span></h3>
                  <svg className="prj-accordion-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
                </button>
                <div className="prj-accordion-body">
                  <div className="prj-detail-info">
                    <div className="prj-info-row">
                      <span className="prj-info-label">Oluşturulma</span>
                      <span className="prj-info-value">{formatDateTime(selected.createdAt)}</span>
                    </div>
                    <div className="prj-info-row">
                      <span className="prj-info-label">Son Güncelleme</span>
                      <span className="prj-info-value">{formatDateTime(selected.updatedAt)}</span>
                    </div>
                    <div className="prj-info-row">
                      <span className="prj-info-label">Versiyon</span>
                      <span className="prj-info-value">{selected.currentVersion || 0}</span>
                    </div>
                    <div className="prj-info-row">
                      <span className="prj-info-label">Dosya Boyutu</span>
                      <span className="prj-info-value">{formatSize(selected.fileSize)}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            </div>)}

            {detailTab === 'chat' && selected && (() => {
              // Tüm paydaşları topla — Person/Firm fields + custom + chatParticipants
              // birleşik, deduped by memberId (varsa) ya da name+role.
              const list = [];
              const seenIds = new Set();
              const seenKeys = new Set();
              const pushItem = (item) => {
                if (!item.name) return;
                if (item.memberId) {
                  if (seenIds.has(item.memberId)) return;
                  seenIds.add(item.memberId);
                }
                const k = `${item.name}|${item.roleLabel || ''}`;
                if (seenKeys.has(k)) return;
                seenKeys.add(k);
                list.push(item);
              };
              for (const pf of PERSON_FIELDS) {
                const dk = personFieldDataKeys(pf.key);
                const name = (editMeta[dk.name] || '').trim();
                if (!name) continue;
                pushItem({
                  fieldKey: pf.key,
                  roleLabel: pf.label,
                  name,
                  type: editMeta[dk.type] || '',
                  memberId: editMeta[dk.memberId] || '',
                  externalId: editMeta[dk.externalId] || '',
                  color: resolveStakeholderColor({ key: pf.key, fallbackSeed: pf.key }),
                });
              }
              for (const cs of (editMeta.customStakeholders || [])) {
                const name = (cs.name || '').trim();
                if (!name) continue;
                pushItem({
                  fieldKey: cs.id,
                  roleLabel: cs.label || 'Paydas',
                  name,
                  type: cs.type || '',
                  memberId: cs.memberId || '',
                  externalId: cs.externalId || '',
                  color: resolveStakeholderColor({ key: cs.id, fallbackSeed: cs.id }),
                });
              }
              // chatParticipants (gerçek üyeler) — eğer henüz listeye eklenmediyse ekle
              for (const p of chatParticipants) {
                if (p.isSelf) continue;
                if (p.id && seenIds.has(p.id)) continue;
                const name = `${p.firstName || ''} ${p.lastName || ''}`.trim();
                if (!name) continue;
                pushItem({
                  fieldKey: p.id,
                  roleLabel: p.role || '',
                  name,
                  type: 'member',
                  memberId: p.id,
                  externalId: '',
                  avatar: p.avatar || '',
                  color: resolveStakeholderColor({ key: null, role: p.role, fallbackSeed: name }),
                });
              }
              const stakeholdersForChat = list;

            return (
              <div className="prj-chat-panel">
                <aside className="prj-chat-sidebar">
                  <button
                    className={`prj-chat-party-item${chatView === 'group' ? ' active' : ''}`}
                    onClick={() => setChatView('group')}
                  >
                    <div className="prj-chat-party-avatar prj-chat-group-avatar">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                    </div>
                    <div className="prj-chat-party-info">
                      <div className="prj-chat-party-name">Grup Sohbeti</div>
                      <div className="prj-chat-party-role">Tüm paydaşlar</div>
                    </div>
                  </button>
                  {stakeholdersForChat.length > 0 && (
                    <div className="prj-chat-party-divider">Özel Sohbet</div>
                  )}
                  {stakeholdersForChat.map(s => {
                    const initials = s.name.split(/\s+/).map(p => p[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
                    const isMember = !!s.memberId;
                    const dmId = s.memberId;
                    const active = isMember && chatView === dmId;
                    return (
                      <button
                        key={s.fieldKey + ':' + s.name}
                        className={`prj-chat-party-item${active ? ' active' : ''}${isMember ? '' : ' prj-chat-party-disabled'}`}
                        onClick={() => { if (isMember) setChatView(dmId); }}
                        title={isMember ? '' : 'Bu paydas Projabze uyesi degil — DM gonderilemez'}
                      >
                        <div
                          className="prj-chat-party-avatar"
                          style={{ background: s.color, color: '#fff' }}
                        >
                          {s.avatar
                            ? <img src={s.avatar.startsWith('http') ? s.avatar : `${API_URL}${s.avatar}`} alt="" />
                            : <span>{initials || '?'}</span>}
                        </div>
                        <div className="prj-chat-party-info">
                          <div className="prj-chat-party-name">{s.name}</div>
                          {s.roleLabel && <div className="prj-chat-party-role">{s.roleLabel}</div>}
                        </div>
                      </button>
                    );
                  })}
                </aside>
                <div className="prj-chat-main">
                <div className="prj-chat-messages">
                  {chatLoading && chatMessages.length === 0 ? (
                    <div className="prj-chat-empty">Yükleniyor...</div>
                  ) : chatMessages.length === 0 ? (
                    <div className="prj-chat-empty">
                      {chatView === 'group'
                        ? 'Henüz grup mesajı yok. Tüm paydaşlarla birlikte konuşmaya başlayın.'
                        : 'Henüz özel mesaj yok. Bu paydaşla özel olarak konuşabilirsiniz.'}
                    </div>
                  ) : (
                    chatMessages.map(msg => {
                      const isMe = msg.senderId === user?.id;
                      const senderName = msg.sender ? `${msg.sender.firstName} ${msg.sender.lastName || ''}`.trim() : 'Bilinmeyen';
                      const senderLabel = msg.senderRole ? `${msg.senderRole} - ${senderName}` : senderName;
                      return (
                        <div key={msg.id} className={`prj-chat-msg ${isMe ? 'me' : ''}`}>
                          {!isMe && (
                            <div className="prj-chat-avatar">
                              {msg.sender?.avatar
                                ? <img src={msg.sender.avatar.startsWith('http') ? msg.sender.avatar : `${API_URL}${msg.sender.avatar}`} alt="" />
                                : <span>{(senderName[0] || '?')}</span>}
                            </div>
                          )}
                          <div className="prj-chat-bubble">
                            {!isMe && <div className="prj-chat-sender">{senderLabel}</div>}
                            {msg.body && <div className="prj-chat-text">{msg.body}</div>}
                            {msg.attachments && msg.attachments.length > 0 && (
                              <div className="prj-chat-attachments">
                                {msg.attachments.map(att => (
                                  <a key={att.id} className="prj-chat-attachment" href={`${API_URL}${att.url}`} target="_blank" rel="noreferrer">
                                    {att.originalName || att.filename}
                                  </a>
                                ))}
                              </div>
                            )}
                            <div className="prj-chat-time">
                              {new Date(msg.createdAt).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                  {chatTypers.length > 0 && (
                    <div className="prj-chat-typing">
                      <span className="prj-chat-typing-dots"><span /><span /><span /></span>
                      <span className="prj-chat-typing-text">
                        {chatTypers.map(t => t.role ? `${t.role} - ${t.name}` : t.name).join(', ')} yaziyor
                      </span>
                    </div>
                  )}
                  <div ref={chatEndRef} />
                </div>
                <div className="prj-chat-input-bar">
                  <button
                    className="prj-chat-attach-btn"
                    onClick={() => chatFileRef.current?.click()}
                    title="Dosya ekle"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
                  </button>
                  <input
                    ref={chatFileRef}
                    type="file"
                    style={{ display: 'none' }}
                    onChange={e => {
                      if (e.target.files[0]) {
                        uploadChatFile(selected.id, e.target.files[0], chatInput, chatView);
                        e.target.value = '';
                      }
                    }}
                  />
                  <input
                    className="prj-chat-input"
                    type="text"
                    placeholder={chatView === 'group' ? 'Gruba mesaj yazın...' : 'Özel mesaj yazın...'}
                    value={chatInput}
                    onChange={e => { setChatInput(e.target.value); if (e.target.value.trim() && chatView === 'group') sendTypingSignal(selected.id); }}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendChatMessage(selected.id, chatInput, chatView); } }}
                    disabled={chatSending}
                  />
                  <button
                    className="prj-chat-send-btn"
                    onClick={() => sendChatMessage(selected.id, chatInput, chatView)}
                    disabled={!chatInput.trim() || chatSending}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                  </button>
                </div>
                </div>
              </div>
            );
            })()}

            {detailTab === 'notes' && selected && (() => {
              // Paydaş kanonik anahtarı: önce authorUserId / participant.id (gerçek
              // kullanıcı kimliği) tercih edilir; yoksa lowercased+trimmed isim
              // kullanılır. Aynı kişinin notlarda farklı role-label ile (eski rol
              // adı, çeviri farkı vb.) listelenmesini engeller.
              const noteCanonicalKey = (n) =>
                n.authorUserId ? `u:${n.authorUserId}` : `n:${(n.authorName || 'Anonim').toLowerCase().trim()}`;
              const participantCanonicalKey = (p) =>
                p.id ? `u:${p.id}` : `n:${(`${p.firstName || ''} ${p.lastName || ''}`.trim() || 'Anonim').toLowerCase()}`;

              const fromNotes = new Map();
              const upsert = (k, base) => {
                const cur = fromNotes.get(k);
                if (cur) {
                  if (!cur.role && base.role) cur.role = base.role;
                  if (!cur.avatar && base.avatar) cur.avatar = base.avatar;
                  return cur;
                }
                fromNotes.set(k, { key: k, count: 0, ...base });
                return fromNotes.get(k);
              };
              for (const n of notesList) {
                const k = noteCanonicalKey(n);
                const e = upsert(k, {
                  name: n.authorName || 'Anonim',
                  role: n.authorRole || null,
                  avatar: n.authorAvatar || null,
                });
                e.count++;
              }
              for (const p of chatParticipants) {
                const k = participantCanonicalKey(p);
                const name = `${p.firstName || ''} ${p.lastName || ''}`.trim() || 'Anonim';
                upsert(k, { name, role: p.role || null, avatar: p.avatar || null });
              }
              const authors = [...fromNotes.values()].sort((a, b) => a.name.localeCompare(b.name, 'tr'));

              // Pafta filtresi için gruplama: tabId yoksa "__general__" kovasına düşer.
              const SHEET_LABELS = ACCESS_SHEETS.reduce((m, s) => { m[s.key] = s.label; return m; }, {});
              const sheetCounts = {};
              for (const n of notesList) {
                const k = n.tabId || '__general__';
                sheetCounts[k] = (sheetCounts[k] || 0) + 1;
              }
              const sheets = Object.keys(sheetCounts)
                .map(k => ({ key: k, label: k === '__general__' ? 'Genel' : (SHEET_LABELS[k] || k), count: sheetCounts[k] }))
                .sort((a, b) => {
                  if (a.key === '__general__') return 1;
                  if (b.key === '__general__') return -1;
                  return a.label.localeCompare(b.label, 'tr');
                });

              const visibleNotes = notesList
                .filter(n => !hiddenNoteAuthors.has(noteCanonicalKey(n)))
                .filter(n => !hiddenNoteSheets.has(n.tabId || '__general__'))
                .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

              // Sıra numarası (paydaş bazlı) — tüm notlar üzerinden hesaplanır
              const ordered = [...notesList].sort((a, b) => {
                const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
                const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
                return ta - tb;
              });
              const indexMap = {};
              const counters = {};
              for (const n of ordered) {
                const k = noteCanonicalKey(n);
                counters[k] = (counters[k] || 0) + 1;
                indexMap[n.id] = counters[k];
              }

              return (
                <div className="prj-chat-panel">
                  <aside className="prj-chat-sidebar">
                    <div className="prj-chat-party-divider" style={{ padding: '.25rem .5rem .4rem' }}>
                      Paydaş Filtresi
                    </div>
                    <div className="prj-notes-filter-quick">
                      <button onClick={() => setHiddenNoteAuthors(new Set())}>Hepsi</button>
                      <button onClick={() => setHiddenNoteAuthors(new Set(authors.map(a => a.key)))}>Hiçbiri</button>
                    </div>
                    {authors.map(a => {
                      const hidden = hiddenNoteAuthors.has(a.key);
                      const initials = a.name.split(/\s+/).map(s => s[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
                      return (
                        <label key={a.key} className={`prj-notes-filter-item${hidden ? ' off' : ''}`}>
                          <input
                            type="checkbox"
                            checked={!hidden}
                            onChange={() => setHiddenNoteAuthors(prev => {
                              const next = new Set(prev);
                              if (next.has(a.key)) next.delete(a.key); else next.add(a.key);
                              return next;
                            })}
                          />
                          <div className="prj-chat-party-avatar">
                            {a.avatar
                              ? <img src={a.avatar.startsWith('http') ? a.avatar : `${API_URL}${a.avatar}`} alt="" />
                              : <span>{initials || '?'}</span>}
                          </div>
                          <div className="prj-chat-party-info">
                            <div className="prj-chat-party-name">{a.name}</div>
                            {a.role && <div className="prj-chat-party-role">{a.role}</div>}
                          </div>
                          {a.count > 0 && <span className="prj-notes-filter-count">{a.count}</span>}
                        </label>
                      );
                    })}
                    {sheets.length > 0 && (
                      <>
                        <div className="prj-chat-party-divider" style={{ padding: '.55rem .5rem .4rem' }}>
                          Pafta Filtresi
                        </div>
                        <div className="prj-notes-filter-quick">
                          <button onClick={() => setHiddenNoteSheets(new Set())}>Hepsi</button>
                          <button onClick={() => setHiddenNoteSheets(new Set(sheets.map(s => s.key)))}>Hiçbiri</button>
                        </div>
                        {sheets.map(s => {
                          const hidden = hiddenNoteSheets.has(s.key);
                          return (
                            <label key={s.key} className={`prj-notes-filter-item prj-notes-filter-sheet${hidden ? ' off' : ''}`}>
                              <input
                                type="checkbox"
                                checked={!hidden}
                                onChange={() => setHiddenNoteSheets(prev => {
                                  const next = new Set(prev);
                                  if (next.has(s.key)) next.delete(s.key); else next.add(s.key);
                                  return next;
                                })}
                              />
                              <div className="prj-notes-filter-sheet-icon">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                              </div>
                              <div className="prj-chat-party-info">
                                <div className="prj-chat-party-name">{s.label}</div>
                              </div>
                              {s.count > 0 && <span className="prj-notes-filter-count">{s.count}</span>}
                            </label>
                          );
                        })}
                      </>
                    )}
                  </aside>
                  <div className="prj-chat-main">
                    <div className="prj-notes-list">
                      {notesLoading && notesList.length === 0 ? (
                        <div className="prj-chat-empty">Yükleniyor...</div>
                      ) : visibleNotes.length === 0 ? (
                        <div className="prj-chat-empty">
                          {notesList.length === 0
                            ? 'Henüz bu projede callout notu yok.'
                            : 'Seçili paydaşların notu yok. Filtreyi değiştirin.'}
                        </div>
                      ) : (
                        visibleNotes.map(n => {
                          const name = n.authorName || 'Anonim';
                          const role = n.authorRole;
                          const idx = indexMap[n.id];
                          const date = (() => {
                            try {
                              return new Date(n.createdAt).toLocaleString('tr-TR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
                            } catch { return ''; }
                          })();
                          const initials = name.split(/\s+/).map(s => s[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
                          const noteColor = colorForRoleLabel(role) || '#888';
                          return (
                            <div
                              key={n.id}
                              className="prj-note-item"
                              style={{ borderLeft: `4px solid ${noteColor}` }}
                            >
                              <div
                                className="prj-note-head"
                                style={{ background: colorWithAlpha(noteColor, 0.18) }}
                              >
                                <div className="prj-note-author">
                                  <div
                                    className="prj-chat-party-avatar"
                                    style={{ background: noteColor, color: '#fff' }}
                                  >
                                    {n.authorAvatar
                                      ? <img src={n.authorAvatar.startsWith('http') ? n.authorAvatar : `${API_URL}${n.authorAvatar}`} alt="" />
                                      : <span>{initials || '?'}</span>}
                                  </div>
                                  <div className="prj-note-author-info">
                                    <span className="prj-note-author-name">
                                      {idx ? `${idx}-${name}` : name}
                                    </span>
                                    {role && <span className="prj-note-author-role">{role}</span>}
                                  </div>
                                </div>
                                <div className="prj-note-meta">
                                  {n.tabId && <span className="prj-note-tab">{SHEET_LABELS[n.tabId] || n.tabId}</span>}
                                  {!n.tabId && <span className="prj-note-tab prj-note-tab-general">Genel</span>}
                                  {date && <span className="prj-note-date">{date}</span>}
                                  {n.targetPoint && (
                                    <button
                                      type="button"
                                      className="prj-note-zoom-btn"
                                      title="Çizimde bu notun konumuna git"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setViewerZoomTarget({
                                          ts: Date.now(),
                                          tabId: n.tabId || null,
                                          point: { x: n.targetPoint.x, y: n.targetPoint.y },
                                        });
                                      }}
                                    >
                                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <circle cx="12" cy="12" r="9"/>
                                        <line x1="12" y1="2" x2="12" y2="5"/>
                                        <line x1="12" y1="19" x2="12" y2="22"/>
                                        <line x1="2" y1="12" x2="5" y2="12"/>
                                        <line x1="19" y1="12" x2="22" y2="12"/>
                                        <circle cx="12" cy="12" r="2"/>
                                      </svg>
                                    </button>
                                  )}
                                </div>
                              </div>
                              {n.rawText && <div className="prj-note-text">{n.rawText}</div>}
                              {/* Yanıt thread'i */}
                              {(n.replies && n.replies.length > 0) && (
                                <div className="prj-note-replies">
                                  {n.replies.map(r => {
                                    const rName = r.authorName || 'Anonim';
                                    const rInitials = rName.split(/\s+/).map(s => s[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
                                    const rColor = colorForRoleLabel(r.authorRole) || '#888';
                                    const rDate = (() => { try { return new Date(r.createdAt).toLocaleString('tr-TR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); } catch { return ''; } })();
                                    const isMine = r.authorUserId && user?.id && r.authorUserId === user.id;
                                    return (
                                      <div key={r.id} className="prj-note-reply">
                                        <div className="prj-note-reply-avatar" style={{ background: rColor, color: '#fff' }}>
                                          {r.authorAvatar
                                            ? <img src={r.authorAvatar.startsWith('http') ? r.authorAvatar : `${API_URL}${r.authorAvatar}`} alt="" />
                                            : <span>{rInitials || '?'}</span>}
                                        </div>
                                        <div className="prj-note-reply-body">
                                          <div className="prj-note-reply-meta">
                                            <span className="prj-note-reply-name">{rName}</span>
                                            {r.authorRole && <span className="prj-note-reply-role">{r.authorRole}</span>}
                                            {rDate && <span className="prj-note-reply-date">{rDate}</span>}
                                            {isMine && (
                                              <button className="prj-note-reply-del" title="Yaniti sil" onClick={() => removeReply(n.id, r.id)}>×</button>
                                            )}
                                          </div>
                                          <div className="prj-note-reply-text">{r.body}</div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                              {/* Yanıt yazma input'u */}
                              <div className="prj-note-reply-input-row">
                                <input
                                  className="prj-note-reply-input"
                                  type="text"
                                  placeholder="Yanit yaz..."
                                  value={(replyDrafts[n.id]?.text) || ''}
                                  onChange={e => setReplyDraft(n.id, { text: e.target.value })}
                                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submitReply(n.id); } }}
                                  disabled={replyDrafts[n.id]?.sending}
                                />
                                <button
                                  type="button"
                                  className="prj-note-reply-send"
                                  onClick={() => submitReply(n.id)}
                                  disabled={!((replyDrafts[n.id]?.text) || '').trim() || replyDrafts[n.id]?.sending}
                                >
                                  {replyDrafts[n.id]?.sending ? '...' : 'Gonder'}
                                </button>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        )}
      </div>
      </div>

      <RightSidebar />
    </div>
  );
};

export default Projects;
